import { z } from "zod";
import { PRESET_SUBJECT_TYPES } from "./presetSubject";
import {
  InquiryAnswerReferenceSchema,
  InquiryCategorySchema,
} from "./inquiry-answer";
// PRODUCT_DEV-673: 活動サマリの集計そのものの型は ./activitySummary.ts が正本。
// API のレスポンス型で再定義せず、そこから借りる（集計とレスポンスの形がズレないように）。
import type {
  ActivityMetrics,
  ActivitySourceKind,
  ActivityTrend,
  MetricsComparison,
  PeriodRange,
} from "./activitySummary";

/*
 * DynamoDBの型
 */
// Meetings
// PRODUCT_DEV-212: nameKana は読み仮名（カタカナ）。音声入力直後はnameKanaにのみ値が入り、
// 編集時にユーザがname（漢字氏名）を任意で追加する。既存DDBレコード後方互換のためoptional。
export const ContactSchema = z.object({
  name: z.string().nullable(), // 氏名（漢字含む）
  nameKana: z.string().nullable().optional(), // 読み仮名（カタカナ）。音声入力で初期値が入る。
  department: z.string().nullable(),
  position: z.string().nullable(),
  // PRODUCT_DEV-655: この方について話された内容（フリーテキスト・行単位で蓄積）。
  // GateKeeper のヒアリングメモ抽出が「明示的にこの人物に帰属された発言のうち
  // preset/マスタ項目に当てはまらないもの」を自動格納し、マスタ同期では既存行に
  // append-dedup マージする（上書きしない）。後方互換のため optional。
  notes: z.string().nullable().optional(),
});
export type DDBContact = z.infer<typeof ContactSchema>;

// PRODUCT_DEV-512: 担当者類似判定(contacts/match-suggest)の「非空 contact」共通述語。
// suggestions の editedIndex はこの述語でフィルタした配列基準のため、client/server で
// 述語が乖離すると index がズレて別人の contact に統合結果が適用される（実害発生済み）。
// 必ず両側でこの関数を使うこと。nameKana のみの contact（音声入力直後）も判定対象。
export const isNonEmptyEditedContact = (c: DDBContact): boolean =>
  (c.name?.trim() ?? "") !== "" ||
  (c.nameKana?.trim() ?? "") !== "" ||
  (c.department?.trim() ?? "") !== "" ||
  (c.position?.trim() ?? "") !== "";

// PRODUCT_DEV-512: 顧客マスタ担当者の「置換更新」指示。
// 担当者には安定IDが無く氏名正規化キーで照合するため、改姓・カナ確定のように
// キーが変わる更新は通常のマージでは既存を見つけられず重複登録になる。
// そこで「旧担当者(oldContact の氏名キー)で既存エントリを特定し、newContact で全置換する」
// 指示を meeting PATCH に optional で添えることで、改姓を含む更新を重複なく反映する。
// この指示は商談レコードには保存しない（patch.ts で剥がす）。
export const ContactMasterUpdateSchema = z.object({
  oldContact: ContactSchema, // 置換対象の既存担当者（識別は氏名→無ければカナの正規化キー）
  newContact: ContactSchema, // 置換後の全項目（改姓・カナ確定を含む）
});
export type ContactMasterUpdate = z.infer<typeof ContactMasterUpdateSchema>;

// PRODUCT_DEV-402: extractedData は preset.fields 駆動の動的構造を許容する。
// レガシーな既定キーは型付きで残しつつ、`.passthrough()` でカスタムプリセットの
// 任意キーを保存できるようにする。company は必須から外す（custom preset で不在のため）。
// 厳密な値検証は API 層 (apps/unizo/api/utils/extractedDataSchema.ts) が
// preset を引いた上で動的に行う。
const ExtractedDataSchema = z
  .object({
    meeting_date: z.string().optional(), // "2025-12-19" などの日付文字列
    company: z.string().nullable().optional(),
    title: z.string().min(1).max(50).nullable().optional(), // 商談記録タイトル
    contacts: z.array(ContactSchema).nullable().optional(),
    needs: z.string().nullable().optional(),
    client_reaction: z.string().nullable().optional(),
    next_action: z.string().nullable().optional(),
    memo: z.string().nullable().optional(),
  })
  .passthrough();

// PRODUCT_DEV-527: CRM コピー文の LLM 要約（preset.clipboardCopyMaxLength 超過時に生成）。
// オンデマンド生成ではなく商談レコードに保存し、同一内容に対して常に同じ文を
// コピーできる決定性を担保する。sourceHash は要約元テキスト（sanitize 後）の
// SHA-256 hex。コピー時に現在のコピー文のハッシュと照合し、不一致なら再生成する。
export const ClipboardCopySummarySchema = z.object({
  text: z.string(), // 要約済みコピー文（API 層が maxLength 以内を保証）
  sourceHash: z.string(), // 要約元テキストの SHA-256 hex
  generatedAt: z.string(), // ISO文字列
});
export type ClipboardCopySummary = z.infer<typeof ClipboardCopySummarySchema>;

export const DDBMeetingSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  // MEETING#<date>#<time>#<id> — date/time は下記のとおり**区切り無し**。
  // 実例: "MEETING#20260531#215740#y8kdev"
  SK: z.string(),
  GSI1PK: z.string(), // TENANT#<tenantId>
  GSI1SK: z.string(), // MEETING#<id>
  id: z.string(),
  title: z.string(),
  /**
   * 商談を採番した日。**区切り無しの "YYYYMMDD"**（例: "20260531"）。
   *
   * ⚠ PRODUCT_DEV-760 まで、ここのコメントは "YYYY-MM-DD" と書かれていたが**誤り**
   * だった（dev/stg/prod の UnizoMeetings を実測して確認。3環境とも全件 "YYYYMMDD"）。
   * この書式は SK にもそのまま入るため、区切り付きの日付で SK のレンジ条件を組むと
   * '-'(0x2D) < '0'(0x30) の順序により**一致0件**になる。範囲 Query を書くときは
   * 必ず区切りを外すこと。
   *
   * なお**表示・集計で使うべきは `meetingDate`（ユーザが直せる実際の商談日）優先**で、
   * こちらはそのフォールバック（`liteRowOccurredOn` / `effectiveMeetingDate` を参照）。
   */
  date: z.string(),
  /** 商談を採番した時刻。**区切り無しの "HHMMSS"**（例: "215740"）。 */
  time: z.string(),
  duration: z.string(), // "1:35" など、フォーマットは文字列として扱う
  // PRODUCT_DEV-402: preset 駆動でフィールド構造が変化するため optional 化。
  // 一覧表示の代表値は API 層で preset.primaryFieldId から導出して埋めるが、
  // フロント (Recording 等) が一時的に `company: null` を送る経路があるため
  // input 互換のため nullable も残す。consumer は `?? undefined` で吸収する。
  company: z.string().nullable().optional(),
  // PRODUCT_DEV-583: 相手顧客(顧客マスタ)の companyId。reviewed 保存時に解決できたら付与する
  // (会社名の文字列突合の曖昧さを解消する非正規化)。旧レコード/未解決は未設定で、
  // マイ顧客の集計は companyId 優先・未設定は company 名突合にフォールバックする。
  companyId: z.string().optional(),
  contacts: z.array(ContactSchema).nullable().optional(), // 任意
  status: z.string(), // 必要になったら enum 化
  extractedData: ExtractedDataSchema,
  // PRODUCT_DEV-589: GateKeeper が実際に受け取った抽出データ（対話モード=ライブ抽出値 /
  // 自由記録=seed）のスナップショット。extractedData は GK 出力で上書きされ入力が失われるため、
  // Playground の「本番同等の再現」用に別途保持する。派生データにつき PatchMeetingSchema には
  // 含めない（PATCH API の書き込み面を広げない）。本項目追加以前の商談には存在しない。
  liveExtractedData: ExtractedDataSchema.optional(),
  createdAt: z.string(), // ISO文字列
  groupId: z.string().nullable(),
  presetId: z.string(),
  sourceMeetingId: z.string().optional(),
  recordingMode: z.enum(["dialogue", "speed"]).optional(),
  reviewed: z.boolean(),
  ownerId: z.string(),
  createdBy: z.string(),
  version: z.number().int(),
  updatedAt: z.string(), // ISO文字列
  updatedBy: z.string(),
  deletedAt: z.string().optional(), // ゴミ箱移動日時（ISO）
  deletedBy: z.string().optional(), // 削除実行者userId
  trashedGroupId: z.string().nullable().optional(), // ゴミ箱移動時の元groupId保存
  ttl: z.number().optional(), // DynamoDB TTL（epoch秒、deletedAt + 30日 / PRODUCT_DEV-576: テスト商談は作成時+1日）
  // PRODUCT_DEV-576(項目1): プリセットの「お試し対話（テスト）」で作成された商談。
  // 通常の記録フローで保存するが、一覧では作成者本人にのみ表示し、ttl(作成+1日)で自動失効する。
  isTest: z.boolean().optional(),
  rawTranscript: z.string().optional(), // 生データの文字起こし文章（ユーザ発話のみ）
  cleanedTranscript: z.string().optional(), // 整形済み文字起こし文章（フィラー除去+辞書補完）
  dialogueLog: z.string().optional(), // 対話ログ全文（AI+User発話、dialogueモードのみ）
  meetingDate: z.string().optional(), // "YYYYMMDD" - ユーザー編集可能な商談日時（未設定時はdateにフォールバック）
  meetingTime: z.string().optional(), // "HHMMSS" - ユーザー編集可能な商談時刻（未設定時はtimeにフォールバック）
  audioContentType: z.string().optional(), // 自由記録モード音声のMIME (audio/webm | audio/mp4)
  // PRODUCT_DEV-527: CRM コピー文の保存済み LLM 要約。API 層（copy-summary）のみが書き込む
  // （PatchMeetingSchema には含めず、フロントから直接更新させない）。
  clipboardCopySummary: ClipboardCopySummarySchema.optional(),
  // PRODUCT_DEV-531: Sansanコンタクト連携で登録済みになった日時(ISO)。API層(sansan-register)
  // のみが書き込む（PatchMeetingSchema には含めない）。UIの「登録済」表示・再送警告に使う。
  sansanRegisteredAt: z.string().optional(),
  // PRODUCT_DEV-655: GateKeeper ヒアリングメモ抽出（第3コール）が書き込む
  // 「顧客本人に明示的に帰属された発言のうち preset/マスタ項目に当てはまらない事項」。
  // 個人商談のみ。商談編集画面で編集可能（PatchMeetingSchema に含める）。
  // 名寄せ確定保存時に individualProfile.otherNotes へ append-dedup で追記される。
  customerHearingNotes: z.string().optional(),
});

export type DDBMeetingContact = z.infer<typeof ContactSchema>;
export type DDBMeeting = z.infer<typeof DDBMeetingSchema>;

// PRODUCT_DEV-531: Sansanコンタクト登録APIのリクエストボディ。
// memo 本文はフロントが renderCopyTemplate で構築済みのものを渡す（copy-summary と同方式）。
export const SansanRegisterRequestSchema = z.object({
  memo: z.string(),
});
export type SansanRegisterRequest = z.infer<typeof SansanRegisterRequestSchema>;

export const SansanRegisterResponseSchema = z.object({
  success: z.boolean(),
  reportId: z.string().optional(),
  registeredAt: z.string(),
});
export type SansanRegisterResponse = z.infer<
  typeof SansanRegisterResponseSchema
>;

// PRODUCT_DEV-493: 編集ログ計測（プリセット単位の修正率・項目別編集回数）
// hot store: 前後値を含む詳細イベントを保持し、DynamoDB TTL で 7日経過後に消失する。
// 永続的な集計（項目別編集回数・修正率）は admin 側の日次 aggregator が S3 に書き出す。
export const EditEventChangeSchema = z.object({
  fieldId: z.string(),
  label: z.string().optional(), // preset.fields 由来の表示ラベル（解決できた場合）
  before: z.string().nullable().optional(), // 文字列化した変更前値（複雑な値は JSON 文字列）
  after: z.string().nullable().optional(), // 文字列化した変更後値
});
export type DDBEditEventChange = z.infer<typeof EditEventChangeSchema>;

export const DDBEditEventSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#<YYYY-MM-DD(JST)>
  SK: z.string(), // EDIT#<ISO timestamp>#<ulid>
  tenantId: z.string(),
  presetId: z.string(),
  meetingId: z.string(),
  actorUserId: z.string(),
  eventKind: z.enum(["field_edit", "register_transition"]),
  // eventKind === "field_edit" のみ
  changedFieldIds: z.array(z.string()).optional(),
  changes: z.array(EditEventChangeSchema).optional(),
  // eventKind === "register_transition" のみ（仮登録→本登録時、同リクエストで項目編集を伴ったか）
  reviewedTransition: z.enum(["with_edit", "without_edit"]).optional(),
  createdAt: z.string(), // ISO8601
  ttl: z.number().int(), // epoch秒（createdAt + 7日）
});
export type DDBEditEvent = z.infer<typeof DDBEditEventSchema>;

// PRODUCT_DEV-434: 自然言語商談検索（AIエージェント検索）
export const MeetingFilterInterpretationSchema = z.object({
  company: z.string().optional(),
  contactName: z.string().optional(),
  /**
   * 記録者（自社ユーザ）の名前。「本蔵が記録した商談」の "本蔵"。
   *
   * `contactName`（取引先側の担当者）とは**別軸**。前者は商談の相手、こちらは
   * 商談を入力した社内の人で、突き合わせ先も searchText ではなくテナントの
   * ユーザ一覧 → `MeetingLiteRow.createdBy` になる。
   *
   * ここには**名前をそのまま**入れる（LLM は userId を知らない）。userId への
   * 解決はサーバ側で行う。
   */
  recordedBy: z.string().optional(),
  /**
   * **役割が言い回しから決まらない人名。**「クスマ素子さんが実施した商談」の "クスマ素子"。
   *
   * 日本語では「〜さんが実施した」「〜さんの商談」のように、その人が**記録した社内の人**
   * なのか**会った相手**なのかが文だけでは決まらない言い方がある。無理にどちらかへ倒すと
   * 外したときに気づけないので、**決まらないことをそのまま**この枠で受ける。
   *
   * 役割の判定はサーバ側が**データで**行う（テナントのユーザ名簿に載っていれば記録者、
   * 載っていなければ取引先の担当者）。言い回しの列挙に頼らないので、新しい動詞が
   * 出てきても壊れない。
   */
  personName: z.string().optional(),
  dateFrom: z.string().optional(), // "YYYY-MM-DD" (JST)
  dateTo: z.string().optional(),
  reviewed: z.boolean().optional(),
  status: z.enum(["active", "trashed", "disabled"]).optional(),
  groupId: z.string().optional(),
  semanticQuery: z.string().optional(), // Stage 3 でベクトル検索に使用
  keywords: z.array(z.string()).optional(),
});
export type MeetingFilterInterpretation = z.infer<
  typeof MeetingFilterInterpretationSchema
>;

export const MeetingSearchRequestSchema = z.object({
  query: z.string().min(1).max(500),
  limit: z.number().int().min(1).max(100).optional(),
  groupId: z.string().optional(),
  rerank: z.boolean().optional(), // Stage 3 で LLM rerank の有効化
});
export type MeetingSearchRequest = z.infer<typeof MeetingSearchRequestSchema>;

/**
 * PRODUCT_DEV-620: AI 回答が引用した記録の種別。
 * 商談記録以外（議事録/メモ/ToDo）も引用できるようにするため導入した。
 */
export const SearchRecordKindSchema = z.enum([
  "meeting",
  "minutes",
  "memo",
  "todo",
]);
export type SearchRecordKind = z.infer<typeof SearchRecordKindSchema>;

/** PRODUCT_DEV-620: 引用元の記録（種別 + id）。UI はこれを引いてカセットを描く */
export const CitedRecordSchema = z.object({
  kind: SearchRecordKindSchema,
  id: z.string(),
});
export type CitedRecord = z.infer<typeof CitedRecordSchema>;

/**
 * PRODUCT_DEV-434: 引用元の「箇所」。
 *
 * S3 Vectors への移行で近傍検索が**チャンク単位**で返るようになり、どのチャンクが
 * 最も近かったかとその本文が追加コストなしで手に入るようになった（本文は非フィルタ
 * メタデータとして格納してあり、クエリの戻りに含まれる）。これにより回答の根拠を
 * 「この商談」ではなく「この商談のこの箇所」まで示せる。
 *
 * 追加専用フィールドなので、既存 consumer は無視すれば従来どおり動く。
 */
export const CitedChunkSchema = z.object({
  kind: SearchRecordKindSchema,
  id: z.string(),
  /** レコード内でのチャンク位置。0 は構造化サマリ、1 以降が文字起こし。 */
  chunkIdx: z.number().int(),
  /** 該当チャンクの本文（表示用に呼び出し側で切り詰める）。 */
  text: z.string(),
});
export type CitedChunk = z.infer<typeof CitedChunkSchema>;

export const MeetingSearchResponseSchema = z.object({
  /**
   * 該当した商談を関連度順に `limit` 件まで。**引用された商談だけではない**。
   *
   * PRODUCT_DEV-434: 一時期ここを `citedRecords` の商談分だけに絞っていたが、
   * 回答プロンプトは引用を 3 件までに制限しているため、回答文が「18 件です」と
   * 言うのに画面には 3 件しか出ない、という食い違いになっていた。
   * **引用は「根拠として挙げたもの」で、該当一覧とは別の概念**。
   * どれが引用かは `citedMeetingIds` / `citedRecords` で判別する。
   */
  meetings: z.array(DDBMeetingSchema.partial()),
  interpretation: MeetingFilterInterpretationSchema,
  /** 該当した商談の総数（`limit` を掛ける前）。`meetings.length` とは一致しない。 */
  total: z.number().int(),
  truncated: z.boolean(),
  /** PRODUCT_DEV-434: LLM が上位商談を根拠に生成する自然言語回答 */
  answer: z.string().optional(),
  /**
   * PRODUCT_DEV-434: answer の根拠となった商談 id（重要度順）。
   * PRODUCT_DEV-620 以降は商談以外も引用しうるため `citedRecords` が正本だが、
   * 既存 consumer（/chat）の後方互換のため商談分だけをここにも引き続き入れる。
   */
  citedMeetingIds: z.array(z.string()).optional(),
  /** PRODUCT_DEV-620: 種別つきの引用元（商談/議事録/メモ/ToDo を横断） */
  citedRecords: z.array(CitedRecordSchema).optional(),
  /**
   * PRODUCT_DEV-434: 引用元の「箇所」。意味検索で最も近かったチャンクを
   * レコードごとに 1 件返す。ベクトル検索が無効／失敗した場合は空になる。
   */
  citedChunks: z.array(CitedChunkSchema).optional(),
});

/**
 * PRODUCT_DEV-620: 引用された議事録 / メモ / ToDo は、各一覧 API が返すのと
 * **同じ型**（`MinutesListItem` / `MemoItem` / `TodoItem`）をそのまま返す。
 *
 * ここで別形を再定義すると、同じ形が contract・API・web の 3 箇所に分散し、
 * 片方だけ変えたときに静かにズレる（web 側が欠けたフィールドを偽の必須値で
 * 埋め直す羽目になっていた）。これらは zod スキーマを持たない既存 interface
 * なので、型だけを合成する（このスキーマはレスポンス生成の型付け用であり
 * `parse()` はされない）。
 */
export type MeetingSearchResponse = z.infer<
  typeof MeetingSearchResponseSchema
> & {
  citedMinutes?: MinutesListItem[];
  citedMemos?: MemoItem[];
  citedTodos?: TodoItem[];
};

// ── PRODUCT_DEV-613: 商談軽量索引 (lite index) ─────────────────────────────
// 一覧/検索/顧客件数を数万件規模へスケールさせるための「共有・軽量索引」。
// テナント単位で S3 に材料化し DDB Streams で増分維持、クライアントが読んで
// 即時フィルタ/件数/集計/ページングを行う。本文(transcript)は載せない。
// isTest(作成者限定・一時)は共有索引から除外する。表示忠実度のため、識別情報
// (isIndividual/displayName) と表示タイトルは索引生成時に precompute する。
export const MeetingLiteRowSchema = z.object({
  id: z.string(),
  date: z.string(), // 採番日 "YYYYMMDD"
  time: z.string(), // 採番時刻 "HHMMSS"
  meetingDate: z.string().optional(), // ユーザ編集の商談日 "YYYYMMDD"（effective date 算出用）
  meetingTime: z.string().optional(), // ユーザ編集の商談時刻 "HHMMSS"
  displayTitle: z.string(), // extractedData.title || title || "" （表示用・生）
  company: z.string().nullable().optional(), // 生: 仮情報判定/法人名完全一致フィルタ/お気に入りメタ
  companyId: z.string().optional(), // 顧客件数(B)集計キー
  isIndividual: z.boolean(), // precompute: 個人商談か（カード名行のアイコン分岐）
  displayName: z.string().optional(), // precompute: カード名行の表示名（個人氏名 or 法人名）
  groupId: z.string().nullable().optional(), // グループフィルタ/フォルダラベル
  reviewed: z.boolean(), // 仮情報/状態フィルタ
  status: z.string(), // 整形中スピナー等（索引は基本 active のみ）
  ttl: z.number().optional(), // 自動削除予定ラベル
  createdBy: z.string(), // 作成者フィルタ/お気に入りメタ・isTest の per-viewer 絞り込み
  // PRODUCT_DEV-613: お試し対話のテスト商談。索引には含めるが、配信 API が
  // 「isTest かつ作成者≠閲覧者」を落とす（作成者本人のみ一覧に出る）。顧客件数集計は
  // isTest を数えない（テスト商談は実商談ではない）。
  isTest: z.boolean().optional(),
  updatedAt: z.string().optional(), // 一覧の更新日時ソート（お気に入り/履歴）用
  createdAt: z.string().optional(), // updatedAt 欠落時のソートフォールバック用
  // PRODUCT_DEV-613: 顧客件数(B)のサーバ側集計で「最終商談の preset/モード」を出すため。
  presetId: z.string().optional(),
  recordingMode: z.enum(["dialogue", "speed"]).optional(),
  searchText: z.string(), // 正規化連結（キーワード一致用・表示には使わない）
});
export type MeetingLiteRow = z.infer<typeof MeetingLiteRowSchema>;

// GET /meetings/lite-index レスポンス。テナント配下の active・isTest除外 の全行を返す。
export const MeetingLiteIndexResponseSchema = z.object({
  success: z.boolean(),
  rows: z.array(MeetingLiteRowSchema),
  generatedAt: z.string().optional(), // 索引の材料化(最終更新)時刻 ISO
});
export type MeetingLiteIndexResponse = z.infer<
  typeof MeetingLiteIndexResponseSchema
>;

// 登録可能なカラムを定義
// PRODUCT_DEV-402: company 必須 → presetId 必須に切替。
// company は API 層で preset.primaryFieldId 由来で埋める。
export const CreateMeetingSchema = DDBMeetingSchema.pick({
  title: true,
  duration: true,
  company: true,
  contacts: true,
  status: true,
  extractedData: true,
  groupId: true,
  presetId: true,
  sourceMeetingId: true,
  recordingMode: true,
  audioContentType: true,
  isTest: true,
})
  .partial()
  .required({ presetId: true })
  .strict();

export type DDBCreateMeeting = z.infer<typeof CreateMeetingSchema>;

// POST /meetings リクエストボディ（conversationLog付き）
export const CreateMeetingWithLogSchema = CreateMeetingSchema.extend({
  conversationLog: z.string().optional(), // 文字起こしログ（非同期処理用）
});
export type CreateMeetingWithLog = z.infer<typeof CreateMeetingWithLogSchema>;

// 更新可能なカラムを定義
export const PatchMeetingSchema = DDBMeetingSchema.pick({
  title: true,
  duration: true,
  company: true,
  contacts: true,
  status: true,
  extractedData: true,
  groupId: true,
  presetId: true,
  reviewed: true,
  rawTranscript: true,
  cleanedTranscript: true,
  dialogueLog: true,
  meetingDate: true,
  meetingTime: true,
  // PRODUCT_DEV-655: ヒアリングメモ（個人）。編集画面でレビュー・修正して保存できる。
  customerHearingNotes: true,
})
  .partial()
  // PRODUCT_DEV-512: 顧客マスタ担当者の置換更新指示（改姓等のキー変更を既存エントリへ反映）。
  // DDBMeetingSchema には含めない（商談レコードには保存しない）。patch.ts で剥がして使う。
  .extend({
    // 1保存=1商談の担当者数に対応する更新指示のため上限を設ける（一括全置換の濫用防止）。
    contactMasterUpdates: z
      .array(ContactMasterUpdateSchema)
      .max(50)
      .optional(),
    // PRODUCT_DEV-542 ③: 顧客マスタへ「同期マージする担当者」を明示する任意リスト。
    // 「今回は変更しない（マスタはそのまま）」を選んだ担当者を商談レコードには残しつつ、
    // マスタ同期からは除外するために使う。未指定なら従来どおり contacts 全件を同期する
    // （後方互換）。DDBMeetingSchema には含めない（patch.ts で剥がして同期にのみ使う）。
    contactsToMerge: z.array(ContactSchema).max(100).optional(),
    // PRODUCT_DEV-543(改): 個人(顧客マスタ)への保存時反映指示。商談中は自動登録せず（仮登録）、
    // 編集画面でユーザが「新規追加 / 既存へ経歴追加 / 今回は変更しない」を確定して送る。
    // 氏名・各項目値は本 patch の extractedData.customer_info（編集後最新）から再取得する
    // ため、ここには PII を載せない。DDBMeetingSchema には含めない（patch.ts で剥がして使う）。
    individualRegistration: z
      .object({
        // "skip" は PRODUCT_DEV-543改で UI から発行されない旧互換値（「登録済データを使用する」は
        // 反映指示を送らない＝個人マスタ不変）。旧データ/旧クライアント互換のため enum から削除しない。
        decision: z.enum(["new", "existing", "skip"]),
        // existing のとき更新対象の個人レコード companyId。
        targetCompanyId: z.string().optional(),
        // PRODUCT_DEV-543改: 経歴追加対象はサーバが customer_info とマスタの差分で自動判定するため
        // fields は実質未使用（常に []）。契約は後方互換のため維持（即時削除しない）。
        fields: z
          .array(z.enum(["occupation", "traits", "age", "income", "family"]))
          .default([]),
      })
      .optional(),
    // PRODUCT_DEV-557: 【非推奨・後方互換のため残置】商談編集画面の「全店顧客番号」更新指示。
    // 当初は本フィールド経由で reviewed 保存の副作用として企業マスタへ反映していたが、
    // 「企業マスタを正本にして直接更新する」方針（Backlog #763426008）に変更したため、
    // web は本フィールドを送らず PATCH /companies/{companyId} で企業マスタを直接更新する。
    // api 側でも本フィールドは剥がして無視する（patch.ts）。ロールアウト中に旧 web が送っても
    // .strict() で 400 にしないため schema には残す。将来クリーンアップで削除予定。
    companySystemIntegrationId: z.string().optional(),
  })
  .strict();

export type DDBPatchMeeting = z.infer<typeof PatchMeetingSchema>;

// PRODUCT_DEV-527: POST /meetings/{meetingId}/copy-summary
// web が構築済みのコピー文（穴埋め後の最終全文）を送り、API が上限以内に
// LLM 要約して商談レコードへ保存 + 返却する。
// 上限はクライアントから受けず、API が meeting.presetId → preset.clipboardCopyMaxLength
// を参照して決める（任意の maxLength を指定した過剰切り詰め要約の保存を防ぐ）。
export const MeetingCopySummaryRequestSchema = z.object({
  text: z.string().min(1).max(20000), // 構築済みコピー文（sanitize は API 側でも冪等に適用）
  // テンプレート先頭の固定見出しブロック（例:「【定型文種類】\n＜...＞」）。
  // text の先頭と一致する場合のみ、要約後も一字一句保全される（LLM 任せにしない）。
  fixedHeader: z.string().max(2000).optional(),
});
export type MeetingCopySummaryRequest = z.infer<
  typeof MeetingCopySummaryRequestSchema
>;

export const MeetingCopySummaryResponseSchema = z.object({
  summary: ClipboardCopySummarySchema,
  // LLM が 2 回試行しても maxLength に収まらず機械的に切り詰めた場合 true
  truncated: z.boolean(),
  attempts: z.number().int(),
});
export type MeetingCopySummaryResponse = z.infer<
  typeof MeetingCopySummaryResponseSchema
>;

// Presets
const SubFieldSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  enabled: z.boolean().optional(),
});

export type DDBSubField = z.infer<typeof SubFieldSchema>;

// PRODUCT_DEV-543(改): プリセットの「情報種別」。company=企業情報 / individual=個人情報 / none=いずれでもない。
// 種別ごとのフィールド相互排他ルールは presetSubject.ts の validatePresetSubjectFields に集約。
export const PresetSubjectTypeSchema = z.enum(PRESET_SUBJECT_TYPES);
export type PresetSubjectTypeValue = z.infer<typeof PresetSubjectTypeSchema>;

export const FieldReferenceSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  status: z.string().optional(),
  value: z.string().optional().nullable(),
  question: z.string().optional(),
  description: z.string().optional(),
  icon: z.string().optional(),
  /**
   * @deprecated PRODUCT_DEV-430: voiceOrder/displayOrder へ移行。
   * 後方互換: voiceOrder/displayOrder が未設定の場合のフォールバックとして使用。
   */
  order: z.number().optional(),
  /** PRODUCT_DEV-430: AIが音声で質問する順序（未設定時は order にフォールバック） */
  voiceOrder: z.number().optional(),
  /** PRODUCT_DEV-430: 画面で表示する順序（未設定時は order にフォールバック） */
  displayOrder: z.number().optional(),
  enabled: z.boolean().optional(),
  locked: z.boolean().optional(),
  $ref: z.string().optional(),
  subFields: z.array(SubFieldSchema).optional(),
});

export type DDBFieldReference = z.infer<typeof FieldReferenceSchema>;

// SpeedModeGuide（自由記録モード用ガイドブロック）
export const SpeedModeGuideItemSchema = z.object({
  id: z.string(),
  label: z.string(),
  subItems: z.array(z.string()),
});

export type SpeedModeGuideItem = z.infer<typeof SpeedModeGuideItemSchema>;

/**
 * PRODUCT_DEV-684: プリセットのライフサイクル。
 *
 * - 未設定（undefined）= 従来どおり利用可能。**既存の全レコードがこれに当たる**ため後方互換。
 * - `draft` = AI 作成途中の下書き。録音・部署設定・データ出力のどこにも出さない。
 * - `active` = 明示的に有効化済み（未設定と同義。UI で「有効化」した結果を残すために持つ）。
 */
export const PRESET_STATUSES = ["draft", "active"] as const;
export const PresetStatusSchema = z.enum(PRESET_STATUSES);
export type PresetStatus = z.infer<typeof PresetStatusSchema>;

/**
 * 下書き（未完成）プリセットか。
 *
 * **下書きを隠す一次防御は `GET /presets` の既定除外**（`?includeDrafts=true` で opt-in）。
 * 論理削除の除外がクライアント 8 箇所の `!p.isDelete` に分散している構造をそのまま踏襲すると、
 * 1 箇所書き忘れただけで半完成のプリセットが録音画面に出るため、サーバ側で既定 deny にしてある。
 * この述語は **API を経由せず repository を直読みする経路**（データ出力の列カタログなど）で使う。
 *
 * 併せて `POST /meetings` は下書き presetId を 400 で拒否し、`PATCH /presets/{id}` の
 * status 変更は sysadmin 限定 + 有効化時に項目構成を検証する（多層防御）。
 */
export const isDraftPreset = (p: { status?: string | null }): boolean =>
  p.status === "draft";

export const StructuredPromptSchema = z.object({
  role: z.string(),
  absoluteRules: z.array(z.string()),
  rules: z.array(z.string()),
  initialAction: z.string().optional(),
  conversationExamples: z
    .array(
      z.object({
        trigger: z.string(),
        response: z.string(),
        forCopyMode: z.boolean().optional(),
      }),
    )
    .optional(),
});

export type StructuredPrompt = z.infer<typeof StructuredPromptSchema>;

export const DDBPresetSchema = z
  .object({
    PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
    SK: z.string().regex(/^PRESET#[^#]+$/, 'SK must be "PRESET#{presetId}"'),
    tenantId: z.string(),
    presetId: z.string(),
    order: z.number(),
    name: z.string(),
    description: z.string(),
    icon: z.string(),
    category: z.string(),
    systemPrompt: z.string(),
    fields: z.array(FieldReferenceSchema),
    readonly: z.boolean(),
    author: z.string().optional(),
    version: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    productTemplateId: z.string().optional(),
    isDelete: z.boolean().optional(),
    // PRODUCT_DEV-355: ピン留め時刻 ISO（テナント共有）。未設定/null = 非ピン。
    // 一覧はピン群を pinnedAt 昇順（先にピンしたものが上）→ 非ピン群を updatedAt 降順で表示。
    pinnedAt: z.string().nullable().optional(),
    realtimeModel: z.string().optional(),
    realtimeConfigId: z.string().optional(),
    dynamicContextSettings: z
      .object({
        enableCompanySearch: z.boolean().default(true),
        // PRODUCT_DEV-561: 個人検索（録音中に個人マスタを氏名で照合）。企業検索とは同時 ON 不可
        // （排他検証は presetSubject.validateSearchModeExclusivity に集約）。情報種別=個人
        // プリセットでのみ有効。既定 false = 従来挙動（回帰ゼロ）。
        enableIndividualSearch: z.boolean().default(false),
        enableDictionary: z.boolean().default(true),
      })
      .optional(),
    structuredPrompt: StructuredPromptSchema.optional(),
    speedModeGuide: z.array(SpeedModeGuideItemSchema).optional(),
    // PRODUCT_DEV-402: 一覧表示用の代表値フィールド id（fields[].id または $ref 末尾）。
    // 未設定時は API 層で extractedData.company → 空文字 にフォールバック。
    primaryFieldId: z.string().optional(),
    // PRODUCT_DEV-543(改): 情報種別（企業/個人/いずれでもない）。未設定の既存プリセットは
    // 種別検証の対象外（後方互換）。設定すると presetSubject の相互排他ルールが効く。
    // null は「未設定へ戻す」(Patch でクリア) を許容するため nullable。
    subjectType: PresetSubjectTypeSchema.nullable().optional(),
    // PRODUCT_DEV-478: 商談詳細「コピーする」で生成する CRM 貼り付け用テキストの定型文。
    // `{{作成者}}` 等のスカラーと `{{#担当者}}...{{/担当者}}` の繰り返しブロックを持つ
    // プレースホルダ文字列（描画は web の renderCopyTemplate / 共通 renderTemplate）。
    // 未設定時はコピー機能が従来のハードコード形式にフォールバックする（後方互換）。
    // 注意: 録音フローの copyMode（過去商談からの引き継ぎ）とは無関係の別概念。
    clipboardCopyTemplate: z.string().optional(),
    // PRODUCT_DEV-527: CRM コピー文の文字数上限（例: 連携先 CRM の上限が 700 文字）。
    // コピー全文（URL 込み・コードポイント数）がこれを超えると LLM 要約が起動する。
    // 未設定 = 要約なし（既存挙動）。Patch で「解除」を null として SET するため
    // 実データ互換で nullable も許容する（consumer は falsy 判定 / ?? undefined で吸収）。
    clipboardCopyMaxLength: z.number().int().min(100).max(5000).nullable().optional(),
    // PRODUCT_DEV-684: ライフサイクル（下書き/有効）。未設定 = 従来どおり利用可能（後方互換）。
    status: PresetStatusSchema.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    // tenantId/presetId と PK/SK の整合性チェック（事故防止）
    const expectedPK = `TENANT#${v.tenantId}`;
    const expectedSK = `PRESET#${v.presetId}`;

    if (v.PK !== expectedPK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["PK"],
        message: `PK must equal ${expectedPK}`,
      });
    }
    if (v.SK !== expectedSK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["SK"],
        message: `SK must equal ${expectedSK}`,
      });
    }
  });

export type DDBPreset = z.infer<typeof DDBPresetSchema>;

export const PatchPresetSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  icon: z.string().optional(),
  category: z.string().optional(),
  order: z.number().optional(),
  readonly: z.boolean().optional(),
  systemPrompt: z.string().optional(),
  productTemplateId: z.string().optional(),
  realtimeModel: z.string().optional(),
  realtimeConfigId: z.string().optional(),
  dynamicContextSettings: z
    .object({
      enableCompanySearch: z.boolean().default(true),
      // PRODUCT_DEV-561: 個人検索（企業検索と排他・情報種別=個人限定・既定 false）。
      enableIndividualSearch: z.boolean().default(false),
      enableDictionary: z.boolean().default(true),
    })
    .optional(),
  structuredPrompt: StructuredPromptSchema.optional(),
  speedModeGuide: z.array(SpeedModeGuideItemSchema).optional(),
  fields: z
    .array(FieldReferenceSchema.extend({ id: z.string().optional() }))
    .optional(),
  // PRODUCT_DEV-402: 一覧表示用の代表値フィールド id を更新可能に。
  // null/空文字は「未設定（フォールバック動作）」として扱う。
  primaryFieldId: z.string().nullable().optional(),
  // PRODUCT_DEV-543(改): 情報種別（企業/個人/いずれでもない）。null=未設定へ戻す（クリア）。
  subjectType: PresetSubjectTypeSchema.nullable().optional(),
  // PRODUCT_DEV-478: CRM コピー定型文テンプレート。空文字は「未設定（従来形式にフォールバック）」。
  clipboardCopyTemplate: z.string().optional(),
  // PRODUCT_DEV-527: コピー文の文字数上限。null は「未設定（要約なし）に戻す」。
  clipboardCopyMaxLength: z.number().int().min(100).max(5000).nullable().optional(),
  // PRODUCT_DEV-355: 論理削除フラグ（復元時に false を送る）。
  isDelete: z.boolean().optional(),
  // PRODUCT_DEV-355: ピン留め時刻 ISO。ピン = now、解除 = null。
  pinnedAt: z.string().nullable().optional(),
  // PRODUCT_DEV-684: 下書き → 有効化（`{status:"active"}`）や、AI 作成中の下書き更新に使う。
  status: PresetStatusSchema.nullable().optional(),
  /**
   * PRODUCT_DEV-679: 楽観ロック用。編集開始時点の `updatedAt` を送ると、
   * その間に他者が更新していた場合 409 で弾く（送らなければ従来どおり last-write-wins）。
   *
   * `version` を使わないのは、Presets の version が string 型で
   * 既存の数値インクリメント（createExpressionsForUpdate）に載らないため（presets.ts の NOTE 参照）。
   * `updatedAt` なら既存データの移行が要らない。
   * 永続化はしない（更新式には含めない）。
   */
  expectedUpdatedAt: z.string().optional(),
});
export type DDBPatchPreset = z.infer<typeof PatchPresetSchema>;

export const CreatePresetSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional().default(""),
  icon: z.string().optional().default(""),
  category: z.string().optional().default(""),
  systemPrompt: z.string().optional().default(""),
  readonly: z.boolean().optional().default(false),
  realtimeModel: z.string().optional(),
  realtimeConfigId: z.string().optional(),
  dynamicContextSettings: z
    .object({
      enableCompanySearch: z.boolean().default(true),
      // PRODUCT_DEV-561: 個人検索（企業検索と排他・情報種別=個人限定・既定 false）。
      enableIndividualSearch: z.boolean().default(false),
      enableDictionary: z.boolean().default(true),
    })
    .optional(),
  structuredPrompt: StructuredPromptSchema.optional(),
  speedModeGuide: z.array(SpeedModeGuideItemSchema).optional(),
  fields: z
    .array(FieldReferenceSchema.extend({ id: z.string().optional() }))
    .optional()
    .default([]),
  // PRODUCT_DEV-402: 一覧表示用の代表値フィールド id を作成時に指定可能に。
  primaryFieldId: z.string().optional(),
  // PRODUCT_DEV-543(改): 情報種別（企業/個人/いずれでもない）。null は未設定扱い（作成時は無視）。
  subjectType: PresetSubjectTypeSchema.nullable().optional(),
  // PRODUCT_DEV-478: CRM コピー定型文テンプレート。
  clipboardCopyTemplate: z.string().optional(),
  // PRODUCT_DEV-527: コピー文の文字数上限。null = 未設定（編集 UI が空欄を null で送る）。
  clipboardCopyMaxLength: z.number().int().min(100).max(5000).nullable().optional(),
  // PRODUCT_DEV-684: AI 作成フローは `draft` で作成する。未指定 = 従来どおり（利用可能）。
  status: PresetStatusSchema.nullable().optional(),
});
export type CreatePresetRequest = z.infer<typeof CreatePresetSchema>;

// ==== Departments (PRODUCT_DEV-572) ====
// テナント配下の「部署（グループ）」。作成は任意で、未作成なら従来挙動と完全に一致する
// （機能フラグ不要）。部署ごとに「表示Preset（許可リスト）」と「デフォPreset」を絞れる。
// unizo ローカル（所属を shared auth-service の DDBUserTenant には持たせない）。
// 既存の商談グループ（groupId / meeting-groups）とは無関係の別概念。
export const DDBDepartmentSchema = z
  .object({
    PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
    SK: z.string().regex(/^DEPT#[^#]+$/, 'SK must be "DEPT#{deptId}"'),
    tenantId: z.string(),
    deptId: z.string(),
    name: z.string(),
    order: z.number(),
    // デフォPreset。未設定/null = 従来デフォ（order 先頭）にフォールバック。
    defaultPresetId: z.string().nullable().optional(),
    // 表示Preset許可リスト。空配列/未設定 = 全Preset表示（絞り込みなし）。
    visiblePresetIds: z.array(z.string()).optional(),
    isDelete: z.boolean().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    const expectedPK = `TENANT#${v.tenantId}`;
    const expectedSK = `DEPT#${v.deptId}`;
    if (v.PK !== expectedPK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["PK"],
        message: `PK must equal ${expectedPK}`,
      });
    }
    if (v.SK !== expectedSK) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["SK"],
        message: `SK must equal ${expectedSK}`,
      });
    }
  });
export type DDBDepartment = z.infer<typeof DDBDepartmentSchema>;

// 所属レコード（ユーザー×テナント → 部署）。1ユーザー 0〜1 部署。
export const DDBUserDepartmentSchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z.string().regex(/^MEMBER#[^#]+$/, 'SK must be "MEMBER#{userId}"'),
  tenantId: z.string(),
  userId: z.string(),
  departmentId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string().optional(),
});
export type DDBUserDepartment = z.infer<typeof DDBUserDepartmentSchema>;

// リクエストボディ（.strict() は使わない = useApiPost/Patch の tenantId 自動注入で 400 になる罠を回避。
// tenantId は認証コンテキスト由来をハンドラで採用し、body の値は信用しない）。
export const CreateDepartmentSchema = z.object({
  name: z.string().min(1),
  defaultPresetId: z.string().nullable().optional(),
  visiblePresetIds: z.array(z.string()).optional(),
});
export type CreateDepartmentRequest = z.infer<typeof CreateDepartmentSchema>;

export const PatchDepartmentSchema = z.object({
  name: z.string().min(1).optional(),
  order: z.number().optional(),
  defaultPresetId: z.string().nullable().optional(),
  visiblePresetIds: z.array(z.string()).optional(),
  isDelete: z.boolean().optional(),
});
export type PatchDepartmentRequest = z.infer<typeof PatchDepartmentSchema>;

// ユーザーの所属設定/解除。departmentId=null で所属解除。
export const AssignUserDepartmentSchema = z.object({
  departmentId: z.string().nullable(),
});
export type AssignUserDepartmentRequest = z.infer<
  typeof AssignUserDepartmentSchema
>;

// Companies

// PRODUCT_DEV-543: 個人(type:"individual")の属性プロファイル。
// 法人(corporate)には決して設定しない（admin patch / repository / upsert の多層でクリア）。
// すべて optional・後方互換（既存レコードに無くても parse 成功）。
// age/income/occupation は「いつ時点か」が意味を持つため as-of 基準日（登録時=商談完了日,
// JST "YYYY-MM-DD"）を併記し、表示側で経過年や時点注記を計算する。
// PRODUCT_DEV-543(改): 個人プロファイル各項目の時系列履歴エントリ。
// value は表示値（年齢も "45" 等 string で統一）、asOf は基準日（JST "YYYY-MM-DD"。
// 商談記録由来＝商談日 / 顧客マスタ直接更新由来＝更新日(当日)）。
// PRODUCT_DEV-543(改2): source = 登録・更新経路。"meeting"=商談記録由来(meetingId付き) /
// "master"=顧客マスタ直接更新。optional・後方互換（source 無しの既存エントリも parse 成功）。
export const ProfileHistoryEntrySchema = z.object({
  value: z.string(),
  asOf: z.string(),
  source: z
    .object({
      kind: z.enum(["meeting", "master"]),
      meetingId: z.string().optional(), // kind==="meeting" のとき由来商談ID
    })
    .optional(),
});
export type ProfileHistoryEntry = z.infer<typeof ProfileHistoryEntrySchema>;

export const IndividualProfileSchema = z.object({
  occupation: z.string().optional(), // 職業（例: 開発エンジニア）— 最新値
  occupationAsOf: z.string().optional(), // 職業の基準日（JST "YYYY-MM-DD"）
  // @deprecated PRODUCT_DEV-655: 特徴カラムは廃止し otherNotes へ一本化。
  // 新規書き込みは行わない（既存データは移行ツール dev/tools/PRODUCT_DEV-655 で
  // otherNotes へ履歴ごと移設・クリアする）。後方互換 parse のためスキーマからは消さない。
  traits: z.string().optional(), // （旧）特徴（趣味/嗜好/性格/人物像など自由記述）
  ageEstimate: z.number().int().min(0).max(150).optional(), // 推定年齢（最新登録時点）
  ageAsOf: z.string().optional(), // 年齢の基準日（表示時に経過年を加算）
  income: z.string().optional(), // 収入（"500万円" 等、レンジ表現を許すため string）— 最新値
  incomeAsOf: z.string().optional(), // 収入の基準日（年注記に使用）
  family: z.string().optional(), // 家族構成（父・母・妻・子供 など）— 最新値
  // PRODUCT_DEV-655: その他ヒアリングできた事項（フリーテキスト・行単位の蓄積メモ）。
  // 既存5項目のような「最新値置換」ではなく、商談ごとの抽出行を append-dedup で蓄積する。
  // 構造化はしない（後で LLM 等により構造化する前提のメモ領域）。基準日は history 側で保持。
  otherNotes: z.string().optional(),
  // PRODUCT_DEV-543(改): 全項目の時系列履歴（「経歴追加」で選択した項目だけ追記）。
  // 上のフラット項目＝最新値、history＝過去含む全件（新しい順は呼び出し側の表示で整える）。
  // すべて optional・後方互換（履歴の無い既存レコードもそのまま parse 成功）。
  history: z
    .object({
      occupation: z.array(ProfileHistoryEntrySchema).optional(),
      // @deprecated PRODUCT_DEV-655: otherNotes へ移行（新規書き込みなし・移行後クリア）。
      traits: z.array(ProfileHistoryEntrySchema).optional(),
      age: z.array(ProfileHistoryEntrySchema).optional(),
      income: z.array(ProfileHistoryEntrySchema).optional(),
      family: z.array(ProfileHistoryEntrySchema).optional(),
      // PRODUCT_DEV-655: otherNotes の追記ログ。商談由来エントリ=その商談で追加した行のみ、
      // マスタ手修正由来=編集後全文を1件。value/asOf/source の形は既存項目と共通。
      otherNotes: z.array(ProfileHistoryEntrySchema).optional(),
    })
    .optional(),
});
export type IndividualProfile = z.infer<typeof IndividualProfileSchema>;

// PRODUCT_DEV-543(改): 「変更なし」標識（旧仕様）。
// PRODUCT_DEV-543(改2): この標識は**今後書き込まない**（履歴＝実値のみ・1日付1件）。
// 定数は残置し、既存データに残る「（変更なし）」を表示・フラット計算から除外するフィルタにのみ使う。
export const PROFILE_NO_CHANGE = "（変更なし）";

// PRODUCT_DEV-543(改): 経歴追加対象になり得る個人プロファイル項目のキー。
// PRODUCT_DEV-655: "traits"（特徴）は廃止し otherNotes（その他ヒアリングできた事項）へ一本化。
// 定数から除去することで、名寄せ反映・完全一致判定・同日競合検出・比較 UI が一斉に
// traits を対象外にする（既存データは移行ツール dev/tools/PRODUCT_DEV-655 で otherNotes へ移設）。
export const INDIVIDUAL_PROFILE_HISTORY_FIELDS = [
  "occupation",
  "age",
  "income",
  "family",
] as const;
export type IndividualProfileHistoryField =
  (typeof INDIVIDUAL_PROFILE_HISTORY_FIELDS)[number];

export const DDBCompanySchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z.string().regex(/^COMPANY#[^#]+$/, 'SK must be "COMPANY#{companyId}"'),
  tenantId: z.string(),
  companyId: z.string(),
  order: z.number(),
  type: z.enum(["corporate", "individual"]).default("corporate"),
  name: z.string(),
  nameKana: z.string(),
  aliases: z.array(z.string().min(1)).default([]),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string(),
  updatedBy: z.string(),
  isTemporary: z.boolean().optional().default(false),
  gbizCorporateNumber: z.string().optional(),
  location: z.string().optional(),
  // PRODUCT_DEV-212: 商談レビュー時に登録される企業担当者リスト（スナップショットではなくマスタ）
  contacts: z.array(ContactSchema).optional().default([]),
  // PRODUCT_DEV-420: 外部システム連携用ID（CSVアップロード時の重複チェックキー）
  systemIntegrationId: z.string().optional(),
  // PRODUCT_DEV-543: 個人(type:"individual")専用の属性プロファイル（法人では未設定）。
  individualProfile: IndividualProfileSchema.optional(),
  // PRODUCT_DEV-583: マイ顧客の社内担当。ownerId=主担当(編集権限を持つ1名)。
  // 初回reviewed商談の記録者を未所有時のみ自動付与(claimOwnershipIfUnowned)。以後は編集/引き継ぎで変更可。
  // 未設定=未所有(旧レコード/未担当)。担当情報の唯一の正本(逆引き非正規化は作らない)。
  ownerId: z.string().optional(),
  // subAssigneeIds=サブ担当(閲覧のみの追加担当・複数)。未設定は [] 相当として扱う。
  subAssigneeIds: z.array(z.string()).optional(),
});

export type DDBCompany = z.infer<typeof DDBCompanySchema>;

// PRODUCT_DEV-583: マイ顧客ブックマーク（per-user・UnizoCompaniesテーブル内の別アイテム）。
// PK=TENANT#{tenantId}#USER#{userId} は既存の tenant-wide Query(PK=TENANT#{tenantId}) が
// 返さない別パーティションなので衝突しない。companyIds はブックマーク済みの companyId 集合。
export const CustomerBookmarksSchema = z.object({
  PK: z
    .string()
    .regex(/^TENANT#[^#]+#USER#[^#]+$/, 'PK must be "TENANT#{tenantId}#USER#{userId}"'),
  SK: z.literal("CUSTOMER_BOOKMARKS"),
  tenantId: z.string(),
  userId: z.string(),
  companyIds: z.array(z.string()).default([]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CustomerBookmarks = z.infer<typeof CustomerBookmarksSchema>;

// PRODUCT_DEV-512: 法人/個人の判定とクロス参照防止の単一規約。
// 旧レコード(type 未設定)は法人扱いにフォールバックする。検索・候補・名寄せ等の
// 全経路でこのヘルパを使い、安全要件(個人情報のクロス参照防止)を1箇所に集約する。
type CompanyTypeLike = { type?: "corporate" | "individual" | null };
export const getCompanyType = (
  company: CompanyTypeLike,
): "corporate" | "individual" => company.type ?? "corporate";
export const isCorporateCompany = (company: CompanyTypeLike): boolean =>
  getCompanyType(company) === "corporate";

// PRODUCT_DEV-512: 企業名(別名含む)で有効な法人マスタを引く。
// 個人(individual)は対象外＝企業担当者⇔個人のクロス参照を作らない。
export const findActiveCorporateByName = <
  T extends CompanyTypeLike & {
    active: boolean;
    name: string;
    aliases?: string[];
  },
>(
  companies: T[],
  name: string,
): T | undefined =>
  companies.find(
    (c) =>
      c.active &&
      isCorporateCompany(c) &&
      (c.name === name || (c.aliases ?? []).includes(name)),
  );

// PRODUCT_DEV-420: 顧客マスタ CSVアップロード（admin only）
// PRODUCT_DEV-457: 既存企業に対しても上書きするため skip_* を update_* に置換
export const CompanyUploadRowStatusSchema = z.enum([
  "insert",
  "update_system_integration_id_match",
  "update_gbiz_corporate_number_match",
  "error_missing_name",
  "error_invalid_format",
  "error_conflicting_keys",
]);
export type CompanyUploadRowStatus = z.infer<typeof CompanyUploadRowStatusSchema>;

export const CompanyUploadRowSchema = z.object({
  rowNumber: z.number(),
  status: CompanyUploadRowStatusSchema,
  reason: z.string().optional(),
  parsedData: z
    .object({
      name: z.string(),
      nameKana: z.string().optional(),
      aliases: z.array(z.string()).optional(),
      systemIntegrationId: z.string().optional(),
      gbizCorporateNumber: z.string().optional(),
      location: z.string().optional(),
    })
    .optional(),
  existingCompanyId: z.string().optional(),
});
export type CompanyUploadRow = z.infer<typeof CompanyUploadRowSchema>;

export const CompanyUploadPreviewRequestSchema = z.object({
  csv: z.string().min(1),
});
export type CompanyUploadPreviewRequest = z.infer<typeof CompanyUploadPreviewRequestSchema>;

export const CompanyUploadPreviewResponseSchema = z.object({
  success: z.boolean(),
  rows: z.array(CompanyUploadRowSchema),
  summary: z.object({
    total: z.number(),
    insert: z.number(),
    update: z.number(),
    error: z.number(),
    updateBreakdown: z.record(z.string(), z.number()),
  }),
  error: z.string().optional(),
});
export type CompanyUploadPreviewResponse = z.infer<typeof CompanyUploadPreviewResponseSchema>;

export const CompanyUploadExecuteRequestSchema = z.object({
  csv: z.string().min(1),
});
export type CompanyUploadExecuteRequest = z.infer<typeof CompanyUploadExecuteRequestSchema>;

export const CompanyUploadExecuteResponseSchema = z.object({
  success: z.boolean(),
  inserted: z.number(),
  updated: z.number(),
  errors: z.number(),
  rows: z.array(CompanyUploadRowSchema),
});
export type CompanyUploadExecuteResponse = z.infer<typeof CompanyUploadExecuteResponseSchema>;

// gBizInfo 公開企業API レスポンス型
export const GBizInfoCompanySchema = z.object({
  corporateNumber: z.string(),
  name: z.string(),
  nameKana: z.string().optional(),
  location: z.string().optional(),
});
export type GBizInfoCompany = z.infer<typeof GBizInfoCompanySchema>;

/**
 * リクエストボディ, レスポンス型
 */
interface AuthApiRequestBody {
  tenantId: string;
}
/**
 * ミーティング一覧取得API
 * GET /meetings
 * in api/lambda/meetings/get.ts
 */
export type MeetingsRequestBody = AuthApiRequestBody & {};
export interface MeetingsResponse {
  success: boolean;
  meetings: DDBMeeting[];
}

/**
 * ミーティング内容補完API
 * POST /meetings/complete/post.ts
 * in api/lambda/meetings/complete/post.ts
 */
// PRODUCT_DEV-402: ExtractedDataSchema は全キー optional 化済みのため
// .partial() は実質 no-op だが、互換のため `.partial()` の振る舞いを維持。
// `.passthrough()` も継続適用される。
export const ExtractedDataPartialSchema = ExtractedDataSchema.partial();
export const MeetingCompleteRequestBodySchema = z
  .object({
    extractedData: ExtractedDataPartialSchema,
    conversationLog: z.string(),
    presetId: z.string().optional(),
  })
  .strict();

export type ExtractedDataPartial = z.infer<typeof ExtractedDataPartialSchema>;
export type MeetingCompleteRequestBody = AuthApiRequestBody &
  z.infer<typeof MeetingCompleteRequestBodySchema>;

// レスポンス型
export interface MeetingCompleteResponseBody {
  supplementedData: Record<string, unknown> & { title?: string };
  supplementedFields: string[];
  confidence: Record<string, number>;
  // PRODUCT_DEV-655 / PRODUCT_DEV-716: ヒアリングメモ抽出（第3コール・全テナント）。
  // customerHearingNotes=顧客本人（個人商談）分、contactHearingNotes=企業担当者（法人商談）分。
  customerHearingNotes?: string;
  contactHearingNotes?: Array<{ name: string; notes: string }>;
}
export interface MeetingCompleteResponse {
  success: boolean;
  result: MeetingCompleteResponseBody;
}

/**
 * ミーティング作成API
 * POST /meetings/
 * in api/lambda/meetings/post.ts
 */
export type MeetingsPostRequestBody = AuthApiRequestBody &
  DDBCreateMeeting & {
    conversationLog?: string; // 文字起こしログ（非同期処理用）
    /**
     * PRODUCT_DEV-612（連鎖モデル FB）: 対話ログの出どころ。
     *
     * `"minutes"` は議事録本文から作った商談。post.ts が `DialogueCompletionMessage.mode`
     * へ透過し、worker が抽出テンプレートへ箇条書き書式ルールを連結する。
     * 未指定（通常の音声録音）は従来どおり。
     * CreateMeetingSchema は `.strict()` なので、conversationLog と同様に parse 前に剥がすこと。
     */
    mode?: "minutes";
  };
export interface MeetingsPostResponse {
  success: boolean;
  meeting: DDBMeeting;
}

/**
 * ミーティング取得API
 * GET /meetings/[meetingId]
 * in api/lambda/meetings/[meetingId]/get.ts
 */
export type MeetingsMeetingIdRequestBody = AuthApiRequestBody & {};
export interface MeetingsMeetingIdResponse {
  success: boolean;
  meeting: DDBMeeting;
}

/**
 * ミーティング更新API
 * PATCH /meetings/[meetingId]
 * in api/lambda/meetings/[meetingId]/patch.ts
 */
export type MeetingsPatchRequestBody = AuthApiRequestBody &
  DDBPatchMeeting & {
    /**
     * PRODUCT_DEV-612（連鎖モデル）: 振り返り録音の対話ログ。
     *
     * これが載っている PATCH は「既存商談に振り返りを追記する」経路を意味し、
     * patch.ts が status:"processing" にして dialogue-completion キューへ流す
     * （POST /meetings と同じ非同期 GateKeeper パターン）。この経路だけ extractedData は
     * **置換ではなくマージ**で適用される（空値で既存を消さない）。
     * PatchMeetingSchema は `.strict()` なので、post.ts と同様 parse 前に剥がすこと。
     */
    conversationLog?: string;
  };
export interface MeetingsPatchResponse {
  success: boolean;
  meeting: DDBMeeting;
}
/**
 * ミーティング削除API
 * DELETE /meetings/[meetingId]
 * in api/lambda/meetings/[meetingId]/delete.ts
 */
export type MeetingsDeleteRequestBody = AuthApiRequestBody & {};
export interface MeetingsDeleteResponse {
  success: boolean;
  meetingId: string;
}

/**
 * プリセット一覧取得API
 * GET /presets
 * in api/lambda/presets/get.ts
 */
export type PresetsRequestBody = AuthApiRequestBody & {};
export interface PresetsResponse {
  success: boolean;
  presets: DDBPreset[];
}

/**
 * プリセット取得API
 * GET /presets/[presetId]
 * in api/lambda/presets/[presetId]/get.ts
 */
export type PresetsPresetIdRequestBody = AuthApiRequestBody & {};
export interface PresetsPresetIdResponse {
  success: boolean;
  preset: DDBPreset;
}

/**
 * 部署一覧取得API（管理者）
 * GET /admin/departments
 */
export interface DepartmentsResponse {
  success: boolean;
  departments: DDBDepartment[];
}

/**
 * 部署 作成/更新API（管理者）
 * POST /admin/departments, PATCH /admin/departments/[deptId]
 */
export interface DepartmentResponse {
  success: boolean;
  department: DDBDepartment;
}

/**
 * ユーザー所属一覧取得API（管理者）
 * GET /admin/user-departments — テナント内の userId → departmentId マッピング
 */
export interface UserDepartmentsResponse {
  success: boolean;
  assignments: { userId: string; departmentId: string }[];
}

/**
 * ユーザー所属 設定/解除API（管理者）
 * PATCH /admin/user-departments/[userId]
 */
export interface AssignUserDepartmentResponse {
  success: boolean;
}

/**
 * 自分の所属部署取得API（一般ユーザー）
 * GET /my-department — 未所属/部署論理削除済みは department:null
 */
export interface MyDepartmentResponse {
  success: boolean;
  department: {
    deptId: string;
    name: string;
    defaultPresetId?: string | null;
    visiblePresetIds?: string[];
  } | null;
}

/**
 * 顧客マスタ取得API
 * GET /companies
 * in api/lambda/companies/get.ts
 */
export type CompaniesRequestBody = AuthApiRequestBody & {};
export interface CompaniesResponse {
  success: boolean;
  companies: DDBCompany[];
}

/**
 * 顧客マスタ検索API
 * GET /companies/search
 * in api/lambda/companies/search/get.ts
 */
export const CompaniesSearchRequestBodySchema = z
  .object({
    name: z.string(),
    // topはリクエストで受け取るためNumberへのキャストを行う
    top: z.preprocess((x) => {
      const num = typeof x === "string" ? Number(x) : x;
      return Number.isNaN(num) ? undefined : num;
    }, z.number().min(1).max(20).optional()),
    source: z.enum(["db", "gbizinfo", "all"]).optional().default("all"),
    // PRODUCT_DEV-512: 法人/個人の検索を単一typeにスコープし、結果を混在させない
    // （個人情報保護のための双方向クロス参照防止）。未指定は法人検索（後方互換）。
    // type横断("all")は意図的に用意しない。
    type: z.enum(["corporate", "individual"]).optional().default("corporate"),
    // PRODUCT_DEV-680: 音声対話 AI が聞き取った「読み」（全角カタカナ）。name とは別に受け取り、
    // 両方でスコアリングして良い方を採る（api/utils/stringDiff.ts hybridSearch）。
    // AI が音を漢字に起こすと name にも nameKana にも当たらず、用語辞書へエントリを足して
    // 当てにいく運用になっていた。読みを併送することで辞書なしでも顧客マスタに当たる。
    //
    // **`CompanyMatch.nameKana`（レスポンス側 = マスタ登録の読み仮名）とは別物**なので、
    // 「クエリ側の読み」であることが名前で分かるよう `queryKana` にしている。
    // optional なので未送信時の挙動は従来と完全に同一（後方互換）。
    // 長さ上限は編集距離計算のコスト青天井を避けるためのガード（読み仮名に 200 文字は不要）。
    queryKana: z.string().max(200).optional(),
  })
  .strict();

export type CompaniesSearchRequestBody = AuthApiRequestBody &
  z.infer<typeof CompaniesSearchRequestBodySchema>;

export interface CompanyMatch {
  query: string;
  id: string;
  name: string;
  nameKana?: string;
  speakAs: string;
  aliases?: string[];
  score: number;
  matchedField?: string;
  confidence?: number;
  // PRODUCT_DEV-212: 該当顧客マスタの既存担当者一覧。
  // 録音中に AI が同名担当者を新規登録するのを防ぐためのプロンプト注入に使用。
  contacts?: DDBContact[];
}

export interface CompaniesSearchResponse {
  success: boolean;
  result: {
    matches: CompanyMatch[];
    gbizMatches?: GBizInfoCompany[];
    needsConfirmation: boolean;
    topMatch: string | null;
  };
}

/**
 * 顧客マスタ登録API（ユーザ向け）
 * POST /companies
 * in api/lambda/companies/post.ts
 */
export const CreateCompanyRequestSchema = z.object({
  name: z.string().min(1),
  nameKana: z.string().optional(),
  gbizCorporateNumber: z.string().optional(),
  type: z.enum(["corporate", "individual"]).optional().default("corporate"),
});
export type CreateCompanyRequest = z.infer<typeof CreateCompanyRequestSchema>;
export interface CreateCompanyResponse {
  success: boolean;
  company: DDBCompany;
  isExisting?: boolean;
}

/**
 * PRODUCT_DEV-212: 担当者類似判定 API
 * POST /contacts/match-suggest
 * in api/lambda/contacts/match-suggest/post.ts
 *
 * 商談編集完了時に、編集後の contacts と顧客マスタの既存 contacts を LLM で照合し、
 * 同一人物候補を返却する。フロントは候補ありの contact について確認モーダルを出す。
 */
export const ContactsMatchSuggestRequestSchema = z
  .object({
    companyName: z.string().min(1),
    contacts: z.array(ContactSchema),
  })
  .strict();
export type ContactsMatchSuggestRequest = z.infer<
  typeof ContactsMatchSuggestRequestSchema
>;

export const ContactsMatchSuggestCandidateSchema = z.object({
  existingIndex: z.number().int().nonnegative(),
  existingContact: ContactSchema,
  similarity: z.enum(["high", "medium"]),
  reason: z.string().optional(),
});
export type ContactsMatchSuggestCandidate = z.infer<
  typeof ContactsMatchSuggestCandidateSchema
>;

export const ContactsMatchSuggestionSchema = z.object({
  editedIndex: z.number().int().nonnegative(),
  candidates: z.array(ContactsMatchSuggestCandidateSchema),
});
export type ContactsMatchSuggestion = z.infer<
  typeof ContactsMatchSuggestionSchema
>;

export interface ContactsMatchSuggestResponse {
  success: boolean;
  /** 顧客マスタにマッチしなかった場合などは empty で返す */
  suggestions: ContactsMatchSuggestion[];
  /** 比較対象として使った既存 contacts のスナップショット（フロントで index 参照に使う） */
  existingContacts: DDBContact[];
}

/**
 * PRODUCT_DEV-543: 個人(type:"individual")の類似候補サジェスト API
 * POST /individuals/match-suggest
 * in api/lambda/individuals/match-suggest/post.ts
 *
 * 個人商談（preset に customer_info を含む）の編集時、入力中の氏名/フリガナと
 * 顧客マスタの既存「個人」を決定的部分一致（苗字・フリガナ）で照合し、
 * 同一人物候補を返す。フロントは候補プルダウンを出し、選択で人物を上書きする。
 * 企業担当者(contacts/match-suggest)と異なり LLM は不要（決定的部分一致のみ）。
 */
export const IndividualsMatchSuggestRequestSchema = z
  .object({
    name: z.string().optional(), // 氏名（漢字/カナ、部分入力可）
    nameKana: z.string().optional(), // 読み仮名（カタカナ、部分入力可）
  })
  .strict()
  .refine(
    (d) => (d.name ?? "").trim() !== "" || (d.nameKana ?? "").trim() !== "",
    { message: "name または nameKana のいずれかが必要です" },
  );
export type IndividualsMatchSuggestRequest = z.infer<
  typeof IndividualsMatchSuggestRequestSchema
>;

export const IndividualMatchCandidateSchema = z.object({
  companyId: z.string(),
  name: z.string(),
  nameKana: z.string(),
  individualProfile: IndividualProfileSchema.optional(),
  similarity: z.enum(["high", "medium"]),
});
export type IndividualMatchCandidate = z.infer<
  typeof IndividualMatchCandidateSchema
>;

export interface IndividualsMatchSuggestResponse {
  success: boolean;
  /** 部分一致した既存「個人」候補（high→medium 順、最大 N 件） */
  candidates: IndividualMatchCandidate[];
}

/**
 * 顧客マスタ更新API（管理画面向け）
 * PATCH /admin/companies/{companyId}
 * in api/lambda/admin/companies/[companyId]/patch.ts
 */
export const UpdateCompanyRequestSchema = z.object({
  name: z.string().min(1).optional(),
  nameKana: z.string().optional(),
  aliases: z.array(z.string()).optional(),
  active: z.boolean().optional(),
  order: z.number().optional(),
  type: z.enum(["corporate", "individual"]).optional(),
  location: z.string().optional(),
  contacts: z.array(ContactSchema).optional(),
  // PRODUCT_DEV-457: 外部システム連携用ID。空文字列で送るとクリア（undefined と区別）
  systemIntegrationId: z.string().optional(),
  // PRODUCT_DEV-583: 法人番号（マイ顧客の顧客編集画面から更新可能。updateCompany は既に対応済み）
  gbizCorporateNumber: z.string().optional(),
  // PRODUCT_DEV-543: 個人プロファイル（type:"individual" のときのみ反映。corporate では strip）
  // PRODUCT_DEV-543(改2): 反映日の指定は廃止。マスタ直接更新は常にサーバ当日付で経歴追加する。
  individualProfile: IndividualProfileSchema.optional(),
});
export type UpdateCompanyRequest = z.infer<typeof UpdateCompanyRequestSchema>;

/**
 * PRODUCT_DEV-557: 商談編集画面からの「全店顧客番号」(企業マスタ systemIntegrationId) 直接更新。
 * PATCH /companies/{companyId}（非admin・通常利用者）。systemIntegrationId のみ更新可。
 * 機能フラグ companyExternalSystemId 有効テナント・法人(corporate)のみ許可（api 側でゲート）。
 * 空文字=クリア（PRODUCT_DEV-457 と同セマンティクス）。
 * 注: useApiPatch が body に tenantId を注入するため .strict() にしない（未知キーは Zod が strip）。
 */
export const UpdateCompanySystemIntegrationIdRequestSchema = z.object({
  systemIntegrationId: z.string(),
});
export type UpdateCompanySystemIntegrationIdRequest = z.infer<
  typeof UpdateCompanySystemIntegrationIdRequestSchema
>;

/**
 * 企業ヨミガナAI推定API
 * POST /companies/suggest-kana
 * in api/lambda/companies/suggest-kana/post.ts
 *
 * gBizInfoにヨミガナがない場合、企業名からAIでカタカナ読みを推定する。
 */
export const SuggestCompanyKanaRequestSchema = z.object({
  name: z.string().min(1).max(200),
});
export type SuggestCompanyKanaRequest = z.infer<
  typeof SuggestCompanyKanaRequestSchema
>;
export interface SuggestCompanyKanaResponse {
  success: boolean;
  nameKana: string;
}

/**
 * OPENAIのリアルタイム対話APIのセッション取得API
 * GET /openai/realtime/session
 * in api/lambda/openai/realtime/session/get.ts
 */
export const OpenAIRealtimeSessionRequestBodySchema = z
  .object({
    model: z.string().optional(),
    voice: z.string().optional(),
    speed: z.string().optional(),
    instructions: z.string().optional(),
    tools: z.array(z.unknown()).optional(), // any[] 相当
  })
  .strict();

export type OpenAIRealtimeSessionRequestBody = AuthApiRequestBody &
  z.infer<typeof OpenAIRealtimeSessionRequestBodySchema>;

/**
 * OpenAI Realtime API のセッション作成 (ephemeral token) の生レスポンス。
 * GA (`POST /v1/realtime/client_secrets`) は value/expires_at をトップレベルに返し、
 * session.model にモデル名が入る。後方互換のため旧 Beta 形式
 * (`client_secret:{value,expires_at}`, トップレベル `model`) も optional で残す。
 */
export interface OpenAIRealtimeSessionRawResponse {
  // GA shape
  value?: string;
  expires_at?: number;
  session?: {
    model?: string;
    [key: string]: unknown;
  };
  // Beta (deprecated) shape
  client_secret?: {
    value: string;
    expires_at: number;
  };
  model?: string;
}

export interface OpenAIRealtimeSessionResponse {
  success: boolean;
  clientSecret: {
    value: string;
    expiresAt: number;
  };
  model: string;
}

/**
 * Whisper音声テキスト化API
 * POST /openai/whisper/transcribe
 * in api/lambda/openai/whisper/transcribe/post.ts
 */
export const WhisperTranscribeResponseBodySchema = z.object({
  text: z.string(),
  duration: z.number(),
});

export type WhisperTranscribeResponseBody = z.infer<
  typeof WhisperTranscribeResponseBodySchema
>;

// ===== Notification Schemas =====

/** アプリ内通知スキーマ */
export const NotificationSchema = z.object({
  notificationId: z.string(),
  body: z.string(),
  isRead: z.boolean(),
  createdAt: z.string(),
});
export type Notification = z.infer<typeof NotificationSchema>;

/** 通知一覧レスポンス */
export const NotificationListResponseSchema = z.object({
  notifications: z.array(NotificationSchema),
  nextToken: z.string().optional(),
});
export type NotificationListResponse = z.infer<
  typeof NotificationListResponseSchema
>;

/** 未読件数レスポンス */
export const UnreadCountResponseSchema = z.object({
  unreadCount: z.number(),
});
export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

/** EventBridgeイベント detail スキーマ */
export const NotificationEventDetailSchema = z.object({
  tenantId: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  action: z.string(),
  actorUserId: z.string().optional(),
  metadata: z.record(z.string()).optional(),
});
export type NotificationEventDetail = z.infer<
  typeof NotificationEventDetailSchema
>;

export interface WhisperTranscribeResponse {
  success: boolean;
  result: WhisperTranscribeResponseBody;
}

// ==========================================
// 商談グループ (Meeting Groups)
// ==========================================

// DynamoDB商談グループ型
export interface DDBMeetingGroup {
  PK: string; // TENANT#{tenantId}
  SK: string; // GROUP#{groupId}
  GSI1PK: string; // TENANT#{tenantId}
  GSI1SK: string; // GROUP#{createdAt}
  groupId: string;
  tenantId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  status?: string; // "active" | "trashed"（未設定は"active"扱い）
  summary?: string; // LLM生成要約テキスト（人間用）
  summaryForAI?: string; // LLM生成要約テキスト（AI記録用構造化）
  summaryUpdatedAt?: string; // 要約の最終更新日時（ISO8601）
  summaryStatus?: "generating" | "completed" | "failed"; // 要約生成ステータス
  summaryErrorMessage?: string; // 要約生成失敗時のエラーメッセージ
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number;
}

// 商談グループ作成リクエスト
export const CreateMeetingGroupSchema = z.object({
  name: z.string().min(1, "グループ名は必須です").max(100, "グループ名は100文字以内です"),
});

export type CreateMeetingGroupRequest = z.infer<typeof CreateMeetingGroupSchema>;

// 商談グループ更新リクエスト
export const UpdateMeetingGroupSchema = z.object({
  name: z.string().min(1, "グループ名は必須です").max(100, "グループ名は100文字以内です"),
});

export type UpdateMeetingGroupRequest = z.infer<typeof UpdateMeetingGroupSchema>;

// 商談グループ一覧レスポンス
export interface MeetingGroupsResponse {
  success: boolean;
  groups: Array<{
    groupId: string;
    name: string;
    meetingCount: number;
    latestMeetingDate: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
}

// 商談グループ単体レスポンス
export interface MeetingGroupResponse {
  success: boolean;
  group: {
    groupId: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
    summary?: string;
    summaryUpdatedAt?: string;
    summaryStatus?: "generating" | "completed" | "failed";
    summaryErrorMessage?: string;
  };
}

// ==========================================
// 商談バッチ移動 (Meeting Batch Move)
// ==========================================

// バッチ移動リクエスト
export const BatchMoveMeetingsRequestSchema = z.object({
  meetingIds: z.array(z.string()).min(1).max(100),
  groupId: z.string(),
});

export type BatchMoveMeetingsRequest = z.infer<
  typeof BatchMoveMeetingsRequestSchema
>;

// バッチ移動レスポンス
export interface BatchMoveMeetingsResponse {
  success: boolean;
  updatedCount: number;
}

// ==========================================
// 商談バッチ更新 (Batch Patch Meetings)
// ==========================================

// Batch Patch Meetings
export const BatchPatchMeetingsRequestSchema = z.object({
  meetingIds: z.array(z.string()).min(1).max(100),
  status: z.enum(["active", "disabled", "trashed"]).optional(),
  groupId: z.string().nullable().optional(),
});

export type BatchPatchMeetingsRequest = z.infer<
  typeof BatchPatchMeetingsRequestSchema
>;

export interface BatchPatchMeetingsResponse {
  updatedCount: number;
}

// ==========================================
// 商談バッチ削除 (Meeting Batch Delete)
// ==========================================

/**
 * バッチ削除リクエスト
 */
export const BatchDeleteMeetingsRequestBodySchema = z.object({
  meetingIds: z.array(z.string()).min(1, "At least one meeting ID is required"),
  permanent: z.boolean().optional().default(false),
});

export type BatchDeleteMeetingsRequestBody = z.infer<
  typeof BatchDeleteMeetingsRequestBodySchema
>;

/**
 * バッチ削除レスポンス
 */
export const BatchDeleteMeetingsResponseSchema = z.object({
  success: z.boolean(),
  deletedCount: z.number(),
});

export type BatchDeleteMeetingsResponse = z.infer<
  typeof BatchDeleteMeetingsResponseSchema
>;

// ==========================================
// ゴミ箱 (Trash)
// ==========================================

// ゴミ箱一覧レスポンス
export interface TrashListResponse {
  success: boolean;
  meetings: DDBMeeting[];
  groups: DDBMeetingGroup[];
}

// ゴミ箱復元リクエスト
export const TrashRestoreRequestSchema = z.object({
  meetingIds: z.array(z.string()).max(100).optional(),
  groupIds: z.array(z.string()).max(100).optional(),
});

export type TrashRestoreRequest = z.infer<typeof TrashRestoreRequestSchema>;

// ==========================================
// お気に入り (Favorites)
// ==========================================

export const DDBFavoriteSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // MEETING#<meetingId>
  userId: z.string(),
  meetingId: z.string(),
  createdAt: z.string(), // ISO string
  companyName: z.string().optional(),
  title: z.string().optional(),
  reviewed: z.boolean().optional(),
});

export type DDBFavorite = z.infer<typeof DDBFavoriteSchema>;

export const DDBGroupFavoriteSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // GROUP#<groupId>
  userId: z.string(),
  groupId: z.string(),
  createdAt: z.string(), // ISO string
});

export type DDBGroupFavorite = z.infer<typeof DDBGroupFavoriteSchema>;

// ==========================================
// 閲覧履歴 (View History)
// ==========================================

export const DDBViewHistorySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // MEETING#<meetingId>
  userId: z.string(),
  targetId: z.string(),
  targetType: z.literal("meeting"),
  viewedAt: z.string(), // ISO 8601
  companyName: z.string().optional(),
  title: z.string().optional(),
});

export type DDBViewHistory = z.infer<typeof DDBViewHistorySchema>;

// ==========================================
// 顧客レポート (Company Reports)
// ==========================================

/**
 * 8カテゴリ定数
 * 口コミ（エントリー）の分類カテゴリ
 */
export const REPORT_CATEGORIES = [
  { id: "customer_profile", title: "顧客プロファイル", subtitle: "ナラティブトピック", icon: "UserCircle", color: "blue" },
  { id: "pain_points", title: "課題・現状の不満", subtitle: "生声ベースの不満(Pain)", icon: "AlertTriangle", color: "red" },
  { id: "hidden_needs", title: "ニーズ・期待効果", subtitle: "裏テーマ(隠れた動機)", icon: "Eye", color: "purple" },
  { id: "budget_resources", title: "予算・リソース", subtitle: "資金源の実写感", icon: "Wallet", color: "green" },
  { id: "competition", title: "競合・比較状況", subtitle: "現状維持(不作為)の壁", icon: "Shield", color: "orange" },
  { id: "relationships", title: "人物関連・人脈", subtitle: "情緒的属性と力学マップ", icon: "Users", color: "indigo" },
  { id: "decision_process", title: "意思決定・導入基盤", subtitle: "物理的・心理的ハードル", icon: "GitBranch", color: "amber" },
  { id: "risks", title: "取引上の制約・リスク", subtitle: "負け筋のアラート", icon: "AlertOctagon", color: "rose" },
] as const;

export type ReportCategoryId = (typeof REPORT_CATEGORIES)[number]["id"];

export const ReportCategoryIdSchema = z.enum([
  "customer_profile",
  "pain_points",
  "hidden_needs",
  "budget_resources",
  "competition",
  "relationships",
  "decision_process",
  "risks",
]);

/**
 * エントリー（口コミ1件）
 * SK: REPORT_ENTRY#<companyId>#<entryId>
 */
export const DDBReportEntrySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  SK: z.string(), // REPORT_ENTRY#<companyId>#<entryId>
  tenantId: z.string(),
  companyId: z.string(),
  entryId: z.string(), // ULID
  categoryId: z.string(), // ReportCategoryId or custom id
  authorId: z.string(), // userId
  authorName: z.string(), // 非正規化表示名
  content: z.string(),
  contentType: z.enum(["fact", "inference"]), // 事実 or 推論
  isPrivate: z.boolean().default(false), // 非公開フラグ
  source: z.enum(["manual", "voice", "ai_extracted"]),
  meetingId: z.string().optional(), // AI抽出の場合の元商談
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DDBReportEntry = z.infer<typeof DDBReportEntrySchema>;

/** エントリー作成リクエスト */
export const CreateReportEntrySchema = z.object({
  categoryId: z.string().min(1),
  content: z.string().min(1),
  contentType: z.enum(["fact", "inference"]),
  isPrivate: z.boolean().default(false),
  source: z.enum(["manual", "voice", "ai_extracted"]).default("manual"),
  meetingId: z.string().optional(),
});
export type CreateReportEntry = z.infer<typeof CreateReportEntrySchema>;

/** エントリー更新リクエスト */
export const UpdateReportEntrySchema = z.object({
  categoryId: z.string().min(1).optional(),
  content: z.string().min(1).optional(),
  contentType: z.enum(["fact", "inference"]).optional(),
  isPrivate: z.boolean().optional(),
});
export type UpdateReportEntry = z.infer<typeof UpdateReportEntrySchema>;

/**
 * カスタムカテゴリ設定
 * SK: REPORT_CONFIG#default or REPORT_CONFIG#<companyId>
 */
export const ReportCustomCategorySchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  icon: z.string(),
  color: z.string(),
});

export const DDBReportConfigSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  SK: z.string(), // REPORT_CONFIG#default or REPORT_CONFIG#<companyId>
  tenantId: z.string(),
  scope: z.string(), // "default" or companyId
  customCategories: z.array(ReportCustomCategorySchema),
  categoryOrder: z.array(z.string()).optional(), // 全カテゴリの表示順
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string(),
});

export type DDBReportConfig = z.infer<typeof DDBReportConfigSchema>;

/** カテゴリ設定更新リクエスト */
export const UpdateReportConfigSchema = z.object({
  customCategories: z.array(ReportCustomCategorySchema),
  categoryOrder: z.array(z.string()).optional(),
});
export type UpdateReportConfig = z.infer<typeof UpdateReportConfigSchema>;

/**
 * 集約ビュー（AIサマリー）
 * SK: REPORT#<companyId> (既存、構造変更)
 */
const ReportSufficiencyAxisSchema = z.object({
  // PRODUCT_DEV-646: 軸の突合キー。従来は name(日本語表示名)で照合していたため、
  // REPORT_CATEGORIES の title を変えると保存済みレポートの軸が全て 0 になった。
  // 既存レコードには存在しないので optional。読み側は categoryId 優先・name フォールバック。
  categoryId: z.string().optional(),
  name: z.string(),
  score: z.number().min(0).max(100),
});

const ReportSalesActivitySchema = z.object({
  date: z.string(),
  title: z.string(),
  actionNeeded: z.boolean(),
  meetingId: z.string().optional(),
  action: z.string().optional(),
  status: z.string().optional(),
});

const ReportActionHistorySchema = z.object({
  month: z.string(),
  content: z.array(z.string()),
  person: z.string(),
});

export const DDBCompanyReportSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>
  SK: z.string(), // REPORT#<companyId>
  tenantId: z.string(),
  companyId: z.string(),
  status: z.enum(["generating", "completed", "failed"]),
  summary: z
    .object({
      text: z.string(),
    })
    .optional(),
  salesInfo: z
    .object({
      text: z.string(),
      activities: z.array(ReportSalesActivitySchema),
    })
    .optional(),
  sufficiency: z
    .object({
      overall: z.number(),
      axes: z.array(ReportSufficiencyAxisSchema),
    })
    .optional(),
  actionAdvice: z
    .object({
      aiAdvice: z.string(),
      history: z.array(ReportActionHistorySchema),
    })
    .optional(),
  generatedAt: z.string().optional(),
  sourceMetadata: z
    .object({
      meetingCount: z.number(),
      latestMeetingDate: z.string().nullable(),
      entryCount: z.number().optional(),
    })
    .optional(),
  categoryInsights: z
    .array(
      z.object({
        categoryId: z.string(),
        insight: z.string(),
        references: z.array(
          z.object({
            type: z.enum(["meeting", "entry"]),
            id: z.string(),
            label: z.string(),
          }),
        ),
      }),
    )
    .optional(),
  errorMessage: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DDBCompanyReport = z.infer<typeof DDBCompanyReportSchema>;
export type CategoryInsight = NonNullable<DDBCompanyReport["categoryInsights"]>[number];

export interface CompanyReportResponse {
  success: boolean;
  status: "none" | "generating" | "completed" | "failed";
  report?: DDBCompanyReport;
  entries?: DDBReportEntry[];
  config?: DDBReportConfig;
  errorMessage?: string;
}

// ==========================================
// 辞書 (Dictionaries)
// ==========================================

export const DictionaryCategorySchema = z.enum([
  "person_name",
  "product_service",
  "technical_term",
  "department_title",
  "other",
]);
export type DictionaryCategory = z.infer<typeof DictionaryCategorySchema>;

export const DICTIONARY_CATEGORY_LABELS: Record<DictionaryCategory, string> = {
  person_name: "人名",
  product_service: "商品・サービス",
  technical_term: "専門用語",
  department_title: "部署・役職",
  other: "その他",
};

export const DDBDictionarySchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z.string().regex(/^DICT#[^#]+$/, 'SK must be "DICT#{dictionaryId}"'),
  tenantId: z.string(),
  dictionaryId: z.string(),
  word: z.string().min(1).max(100),
  reading: z.string().min(1).max(200),
  readings: z.array(z.string()).optional(),
  formalName: z.string().max(30).optional(),
  meaning: z.string().max(80).optional(),
  category: DictionaryCategorySchema,
  order: z.number(),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type DDBDictionary = z.infer<typeof DDBDictionarySchema>;

export const CreateDictionarySchema = z.object({
  word: z.string().min(1).max(100),
  readings: z.array(z.string().min(1).max(200)).min(1).max(10),
  formalName: z.string().max(30).optional(),
  meaning: z.string().max(80).optional(),
  category: DictionaryCategorySchema,
});
export type CreateDictionaryRequest = z.infer<typeof CreateDictionarySchema>;

export const PatchDictionarySchema = z.object({
  word: z.string().min(1).max(100).optional(),
  readings: z.array(z.string().min(1).max(200)).min(1).max(10).optional(),
  formalName: z.string().max(30).nullable().optional(),
  meaning: z.string().max(80).nullable().optional(),
  category: DictionaryCategorySchema.optional(),
  active: z.boolean().optional(),
  order: z.number().optional(),
  /**
   * PRODUCT_DEV-679: 楽観ロック用。編集開始時点の `updatedAt` を送ると、
   * その間に他者が更新していた場合 409 で弾く（送らなければ従来どおり last-write-wins）。
   * 永続化はしない（更新式には含めない）。
   */
  expectedUpdatedAt: z.string().optional(),
});
export type PatchDictionaryRequest = z.infer<typeof PatchDictionarySchema>;

export interface DictionariesResponse {
  success: boolean;
  dictionaries: DDBDictionary[];
}

export interface DictionaryResponse {
  success: boolean;
  dictionary: DDBDictionary;
}

// Realtime API Config
export const DDBRealtimeConfigSchema = z.object({
  PK: z.string().regex(/^TENANT#[^#]+$/, 'PK must be "TENANT#{tenantId}"'),
  SK: z
    .string()
    .regex(
      /^REALTIME_CONFIG#[^#]+$/,
      'SK must be "REALTIME_CONFIG#{configId}"',
    ),
  tenantId: z.string(),
  configId: z.string(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  order: z.number(),
  active: z.boolean(),
  // Session creation params
  model: z.string(),
  voice: z.string(),
  speed: z.number().min(0.5).max(2.0),
  inputAudioFormat: z.string().optional(),
  outputAudioFormat: z.string().optional(),
  inputAudioNoiseReduction: z.boolean().optional(),
  inputAudioTranscriptionModel: z.string().optional(),
  // Session update params
  turnDetectionType: z.string(),
  turnDetectionThreshold: z.number().min(0).max(1),
  turnDetectionSilenceDurationMs: z.number().min(300).max(3000),
  turnDetectionPrefixPaddingMs: z.number().min(100).max(500),
  turnDetectionEagerness: z.string().optional(),
  turnDetectionInterruptResponse: z.boolean(),
  turnDetectionCreateResponse: z.boolean(),
  // temperature は GA で廃止（送信されない）。旧レコード read 互換のため optional で残置
  temperature: z.number().min(0).max(2).optional(),
  // gpt-realtime-2 は出力上限 32K（1.5 は 4096）。モデル別上限は UI/送信側でクランプ
  maxResponseOutputTokens: z.number().min(50).max(32768),
  modalities: z.array(z.string()),
  // reasoning.effort（gpt-realtime-2 等 reasoning 対応モデルのみ送信）。値: minimal/low/medium/high/xhigh
  reasoningEffort: z.string().optional(),
  // Audit
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});
export type DDBRealtimeConfig = z.infer<typeof DDBRealtimeConfigSchema>;

export const CreateRealtimeConfigSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  model: z.string(),
  voice: z.string(),
  speed: z.number().min(0.5).max(2.0),
  inputAudioFormat: z.string().optional(),
  outputAudioFormat: z.string().optional(),
  inputAudioNoiseReduction: z.boolean().optional(),
  inputAudioTranscriptionModel: z.string().optional(),
  turnDetectionType: z.string(),
  turnDetectionThreshold: z.number().min(0).max(1),
  turnDetectionSilenceDurationMs: z.number().min(300).max(3000),
  turnDetectionPrefixPaddingMs: z.number().min(100).max(500),
  turnDetectionEagerness: z.string().optional(),
  turnDetectionInterruptResponse: z.boolean(),
  turnDetectionCreateResponse: z.boolean(),
  temperature: z.number().min(0).max(2).optional(),
  maxResponseOutputTokens: z.number().min(50).max(32768),
  modalities: z.array(z.string()),
  reasoningEffort: z.string().optional(),
});
export type CreateRealtimeConfigRequest = z.infer<
  typeof CreateRealtimeConfigSchema
>;

export const PatchRealtimeConfigSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  order: z.number().optional(),
  active: z.boolean().optional(),
  model: z.string().optional(),
  voice: z.string().optional(),
  speed: z.number().min(0.5).max(2.0).optional(),
  inputAudioFormat: z.string().optional(),
  outputAudioFormat: z.string().optional(),
  inputAudioNoiseReduction: z.boolean().optional(),
  inputAudioTranscriptionModel: z.string().optional(),
  turnDetectionType: z.string().optional(),
  turnDetectionThreshold: z.number().min(0).max(1).optional(),
  turnDetectionSilenceDurationMs: z.number().min(300).max(3000).optional(),
  turnDetectionPrefixPaddingMs: z.number().min(100).max(500).optional(),
  turnDetectionEagerness: z.string().optional(),
  turnDetectionInterruptResponse: z.boolean().optional(),
  turnDetectionCreateResponse: z.boolean().optional(),
  temperature: z.number().min(0).max(2).optional(),
  maxResponseOutputTokens: z.number().min(50).max(32768).optional(),
  modalities: z.array(z.string()).optional(),
  reasoningEffort: z.string().optional(),
});
export type PatchRealtimeConfigRequest = z.infer<
  typeof PatchRealtimeConfigSchema
>;

/**
 * reasoning.effort を送信可能なリアルタイムモデル（OpenAI Realtime GA）。
 * 非対応モデル（gpt-realtime-1.5 等）に reasoning を送ると 400 で弾かれるため、
 * editor の reasoning UI 表示・session.update への付与はこの一覧でゲートする。
 */
export const REASONING_CAPABLE_REALTIME_MODELS: readonly string[] = [
  "gpt-realtime-2",
];

export interface RealtimeConfigsResponse {
  success: boolean;
  realtimeConfigs: DDBRealtimeConfig[];
}

export interface RealtimeConfigResponse {
  success: boolean;
  realtimeConfig: DDBRealtimeConfig;
}

// ─── Notifications ────────────────────────────────────

export const NotificationTypeEnum = z.enum([
  "meeting_edited",
  "meeting_shared",
  "meeting_deleted",
  "meeting_created",
  "meeting_group_created",
  "meeting_group_updated",
  "meeting_group_deleted",
  "system_announcement",
  "system_urgent",
  // PRODUCT_DEV-459 Phase B: メモ完了時の自己通知 (記録者本人宛)。
  // emit 側は PRODUCT_DEV-424 (メモ機能本体) で実装予定。
  // PRODUCT_DEV-459: 正常完了は原則通知せず、処理が閾値以上かかった場合のみ memo_completed を発火。
  "memo_completed",
  // PRODUCT_DEV-459: 音声メモの文字起こし/保存失敗時の自己通知 (記録者本人宛)。
  "memo_failed",
  // PRODUCT_DEV-583: マイ顧客の主担当を引き継がれた本人宛の通知。
  "customer_handover",
  // PRODUCT_DEV-635: 問い合わせに運営の回答が届いたときの投稿者本人宛の通知（常時通知・設定対象外）。
  // emit 側は admin API（回答承認時に UnizoNotificationEvents へ直接 Put。worker 経路は通らない）。
  "inquiry_answered",
]);
export type NotificationType = z.infer<typeof NotificationTypeEnum>;

export const DDBNotificationSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  notificationId: z.string(),
  userId: z.string(),
  type: NotificationTypeEnum,
  title: z.string(),
  body: z.string(),
  isRead: z.boolean(),
  isUrgent: z.boolean().optional(),
  metadata: z.record(z.string()).optional(),
  createdAt: z.string(),
  ttl: z.number().optional(),
});
export type DDBNotification = z.infer<typeof DDBNotificationSchema>;

export interface NotificationsResponse {
  success: boolean;
  notifications: DDBNotification[];
  unreadCount: number;
  /**
   * PRODUCT_DEV-652: 続きページのカーソル（省略可 = 続きなし）。
   * 通知一覧が最新 50 件で打ち切られ、商談作成通知に自分宛の通知が押し出されて
   * 辿れなくなる問題への対応。optional なので、返さない API / 読まない web とも従来どおり動く。
   */
  nextToken?: string;
}

export interface NotificationUnreadCountResponse {
  success: boolean;
  unreadCount: number;
}

export interface NotificationReadAllResponse {
  success: boolean;
}

// ─── Notification Settings (PRODUCT_DEV-293) ──────────

/**
 * 通知設定カテゴリ
 * Figmaの通知トグルに対応する6カテゴリ。全てboolean、デフォルトtrue。
 * 設定対象外: meeting_group_deleted, system_announcement, system_urgent（常時通知）
 */
export const NotificationCategoriesSchema = z.object({
  meeting_edited: z.boolean(),
  meeting_shared: z.boolean(),
  meeting_deleted: z.boolean(),
  meeting_created: z.boolean(),
  meeting_group_created: z.boolean(),
  meeting_group_updated: z.boolean(),
});
export type NotificationCategories = z.infer<typeof NotificationCategoriesSchema>;

/**
 * 設定対象のNotificationType一覧（Worker内の設定チェック用）
 */
export const SETTINGS_CONTROLLED_TYPES = [
  "meeting_edited",
  "meeting_shared",
  "meeting_deleted",
  "meeting_created",
  "meeting_group_created",
  "meeting_group_updated",
] as const satisfies readonly NotificationType[];

/**
 * カテゴリ別 Web Push 通知設定 (PRODUCT_DEV-461)
 * アプリ内通知(categories)と同じ6カテゴリ。各カテゴリで push を個別ON/OFF。
 * デフォルトは全 false（オプトイン）。push は「アプリ内ONが前提」の部分集合。
 */
export type NotificationPushCategories = NotificationCategories;

/**
 * DDB通知設定レコード
 * PK: TENANT#{tenantId}#USER#{userId}
 * SK: "SETTINGS"
 */
export const DDBNotificationSettingsSchema = z.object({
  PK: z.string(),
  SK: z.literal("SETTINGS"),
  userId: z.string(),
  categories: NotificationCategoriesSchema,
  // PRODUCT_DEV-461: カテゴリ別 push 設定（既存レコードには無いため optional、未定義は全false扱い）
  pushCategories: NotificationCategoriesSchema.optional(),
  updatedAt: z.string(),
});
export type DDBNotificationSettings = z.infer<typeof DDBNotificationSettingsSchema>;

/**
 * PATCH /notification/settings リクエストボディ
 * categories / pushCategories とも任意・部分更新可。
 */
export const PatchNotificationSettingsSchema = z.object({
  categories: NotificationCategoriesSchema.partial().optional(),
  pushCategories: NotificationCategoriesSchema.partial().optional(),
});
export type PatchNotificationSettingsRequest = z.infer<typeof PatchNotificationSettingsSchema>;

export interface NotificationSettingsResponse {
  success: boolean;
  categories: NotificationCategories;
  pushCategories: NotificationPushCategories;
}

// ─── Dialogue Character (PRODUCT_DEV-638 対話キャラクター 個人設定) ───────────
// テーブル: UnizoNotificationEvents を流用（唯一の per-user PK を持つテーブル）
//   PK: TENANT#{tenantId}#USER#{userId} / SK: "SETTINGS#DIALOGUE_CHARACTER"
// キャラ＝見た目+声のセット。voice の解決は web 側レジストリ
// (apps/unizo/web/src/lib/character/dialogueCharacters.ts) が行い、
// セッション発行時に ?voice= で上書きする。未設定ユーザは
// 見た目=female・声=リアルタイムAPI設定のフォールバック。

export const DialogueCharacterIdSchema = z.enum([
  "female",
  "male",
  "elephant",
  "robot",
]);
export type DialogueCharacterId = z.infer<typeof DialogueCharacterIdSchema>;

export const DDBDialogueCharacterSettingSchema = z.object({
  PK: z.string(),
  SK: z.literal("SETTINGS#DIALOGUE_CHARACTER"),
  userId: z.string(),
  characterId: DialogueCharacterIdSchema,
  updatedAt: z.string(),
});
export type DDBDialogueCharacterSetting = z.infer<
  typeof DDBDialogueCharacterSettingSchema
>;

/** PATCH /user-settings/dialogue-character リクエストボディ。null=デフォルトに戻す（設定削除） */
export const PatchDialogueCharacterSettingSchema = z.object({
  characterId: DialogueCharacterIdSchema.nullable(),
});
export type PatchDialogueCharacterSettingRequest = z.infer<
  typeof PatchDialogueCharacterSettingSchema
>;

export interface DialogueCharacterSettingResponse {
  success: boolean;
  /** null = 未設定（フォールバック挙動） */
  characterId: DialogueCharacterId | null;
}

// ─── Inquiries (PRODUCT_DEV-635 問い合わせ) ───────────
// テーブル: UnizoInquiries
//   PK: TENANT#{tenantId}#USER#{userId} / SK: INQUIRY#{createdAt}#{inquiryId}
// AI 回答ドラフトは agent-jobs(jobKind=inquiry-answer) が生成し、admin API が
// lazy read-back で draftAnswer に永続化。運営が承認した answer だけがユーザに見える。

export const InquiryStatusEnum = z.enum(["open", "answered"]);
export type InquiryStatus = z.infer<typeof InquiryStatusEnum>;

export const InquiryDraftStatusEnum = z.enum(["pending", "ready", "failed"]);
export type InquiryDraftStatus = z.infer<typeof InquiryDraftStatusEnum>;

export const InquiryDraftAnswerSchema = z.object({
  text: z.string(),
  references: z.array(InquiryAnswerReferenceSchema).default([]),
  model: z.string().optional(),
  costUsd: z.number().optional(),
  generatedAt: z.string(),
});
export type InquiryDraftAnswer = z.infer<typeof InquiryDraftAnswerSchema>;

/**
 * 回答に添えるイラスト（PRODUCT_DEV-660）。運営が回答時に選ぶ。
 * done = 星を掲げるゾウ（既定）/ apology = 正面のお辞儀（クレーム対応）。
 * 「クレームに笑顔で返す」を避けるための切り替えで、UI の variant 名（inquiryDone 等）は
 * 画面都合なのでここには載せない（表示側でマップする）。
 */
export const InquiryAnswerIllustrationEnum = z.enum(["done", "apology"]);
export type InquiryAnswerIllustration = z.infer<
  typeof InquiryAnswerIllustrationEnum
>;

export const InquiryAnswerViewSchema = z.object({
  text: z.string(),
  references: z.array(InquiryAnswerReferenceSchema).default([]),
  answeredBy: z.string(), // 回答した運営ユーザの userId
  answeredByName: z.string().optional(),
  answeredAt: z.string(),
  // 未設定 = PD-660 以前に回答済みのデータ。表示側は done にフォールバックする
  illustration: InquiryAnswerIllustrationEnum.optional(),
});
export type InquiryAnswerView = z.infer<typeof InquiryAnswerViewSchema>;

export const InquiryAssigneeSchema = z.object({
  userId: z.string(),
  userName: z.string(),
  // PRODUCT_DEV-635 fix2: アサイン時点の顔写真キー（Users.faceImageKey のスナップショット。
  // キーはイミュータブル（PD-615）なので snapshot で安全。表示は admin の /api/avatar/view-urls で解決）
  faceImageKey: z.string().optional(),
});
export type InquiryAssignee = z.infer<typeof InquiryAssigneeSchema>;

export const DDBInquirySchema = z.object({
  PK: z.string(),
  SK: z.string(),
  inquiryId: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  // admin 一覧表示用の denormalize（投稿時点の名称スナップショット）
  userName: z.string(),
  tenantName: z.string(),
  category: InquiryCategorySchema,
  title: z.string(),
  body: z.string(),
  // 「解決しない場合は新しく問い合わせる」経由時の元問い合わせ
  relatedInquiryId: z.string().optional(),
  status: InquiryStatusEnum,
  // 運営の担当者（誰でも付け替え可。引き継ぎ確認は web ダイアログ）
  assignee: InquiryAssigneeSchema.optional(),
  draftStatus: InquiryDraftStatusEnum,
  // AI ドラフトの世代番号。再生成のたびに +1（jobId = `{inquiryId}-r{draftRev}`）
  draftRev: z.number(),
  latestJobId: z.string().optional(),
  draftAnswer: InquiryDraftAnswerSchema.optional(),
  answer: InquiryAnswerViewSchema.optional(),
  // fix3(PD-632取り込み): 回答をユーザが確認した時刻。HOME の回答バナーは
  // status=answered && answerAckedAt なし の最新1件に表示し、✕ or 回答閲覧で ack する
  answerAckedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBInquiry = z.infer<typeof DDBInquirySchema>;

// API view（内部キー PK/SK と運営向け内部情報 draftAnswer/latestJobId を落とす）
export const InquiryViewSchema = DDBInquirySchema.omit({
  PK: true,
  SK: true,
  draftAnswer: true,
  latestJobId: true,
});
export type InquiryView = z.infer<typeof InquiryViewSchema>;

export const CreateInquiryRequestSchema = z.object({
  category: InquiryCategorySchema,
  title: z.string().min(1).max(100),
  body: z.string().min(1).max(4000),
  relatedInquiryId: z.string().max(64).optional(),
  // PRODUCT_DEV-731: 問い合わせ前 AI Q&A から引き継いで投稿されたか（週次レポートの
  // 「AI で解決したか」を測る唯一の手掛かり）。web が Q&A 画面から遷移したときだけ true を
  // 送る自己申告で、サーバは検証しない。**optional 必須**: 古いクライアント・Q&A を通らない
  // 通常投稿は送ってこない（後方互換）。
  fromQa: z.boolean().optional(),
  // 何問 AI に聞いてから運営へ切り替えたか。fromQa が true のときだけ意味を持つ。
  qaTurnCount: z.number().int().min(0).max(100).optional(),
});
export type CreateInquiryRequest = z.infer<typeof CreateInquiryRequestSchema>;

// ─── Inquiry Q&A (PRODUCT_DEV-690 問い合わせ前の AI Q&A) ───────────
// 問い合わせフォームの手前に挟む同期 Q&A。使い方ガイドカタログを根拠に LLM がその場で答え、
// 回答は運営の承認を経ずユーザへ直接届く（上の draftAnswer 経路とはそこが異なる）。
// やりとりは DDB に保存しない。未解決なら web 側が全ターンを本文へ畳んで
// CreateInquiryRequest として投稿するため、問い合わせ側の契約は変更していない。

/**
 * 直近のやりとり 1 往復。web は積み上げた全ターンのうち**末尾 2 件だけ**を送る。
 * 受け口を max(2) で絞るのはサーバ側でトークン量を頭打ちにするため（ガイドカタログ全件を
 * system に積むので、全履歴を渡すと API Gateway の 29 秒キャップに近づく）。
 */
export const InquiryQaTurnSchema = z.object({
  question: z.string().max(500),
  answerText: z.string().max(4000),
});
export type InquiryQaTurn = z.infer<typeof InquiryQaTurnSchema>;

export const InquiryQaRequestSchema = z.object({
  question: z.string().min(1).max(500),
  history: z.array(InquiryQaTurnSchema).max(2).optional(),
});
export type InquiryQaRequest = z.infer<typeof InquiryQaRequestSchema>;

export const InquiryQaResponseSchema = z.object({
  success: z.literal(true),
  answerText: z.string().max(4000),
  // カタログ突合済みの参照だけが入る（未知 id は engine 側で drop 済み）
  references: z.array(InquiryAnswerReferenceSchema).max(5),
});
export type InquiryQaResponse = z.infer<typeof InquiryQaResponseSchema>;

// ─── Inquiry Q&A 利用ログ (PRODUCT_DEV-731) ─────────────────────────
// PRODUCT_DEV-690 は「やりとりを保存しない」設計だったが、運営が「AI に何を聞かれているか」を
// 一切観測できず、ガイド拡充・回答品質の監視・不具合報告の取りこぼし検知ができなかったため、
// 保存する方針へ明示的に反転させた。
//
// 専用テーブル UnizoInquiryQaLogs に置く（UnizoInquiries へ相乗りさせない）。読み手は
// **運営向けの全テナント横断・週次集計 1 本だけ**なので、PK にテナントを入れず日付だけで
// バケットする。これで週次集計が 7 回の Query で完結し、Scan も GSI も要らない。
// テナント単位の抽出が要件化したら GSI_ByTenant を後付けできるよう tenantId は素の属性で持つ。
//
// TTL は貼らない（`ttl` 属性を書かない）＝ログは消えない。テーブル側には
// timeToLiveAttribute だけ宣言してあるので、保持期間を導入したくなったらコードで
// `ttl` を書き始めるだけで済む。
export const DDBInquiryQaLogSchema = z.object({
  PK: z.string(), // DATE#<YYYY-MM-DD(JST)>
  SK: z.string(), // QA#<ISO timestamp>#<uuid> または HANDOFF#<ISO timestamp>#<uuid>
  // "qa" = AI への質問 1 回、"handoff" = そこから運営への問い合わせに進んだ印
  kind: z.enum(["qa", "handoff"]),
  tenantId: z.string(),
  tenantName: z.string(),
  userId: z.string(),
  userName: z.string(),
  createdAt: z.string(), // ISO8601
  jstDate: z.string(), // YYYY-MM-DD(JST)。PK と同じ日付を素の属性でも持つ（集計側が読みやすい）

  // ---- kind === "qa" ----
  question: z.string().max(500).optional(),
  // status === "error"（LLM が落ちた）のときは無い
  answerText: z.string().max(4000).optional(),
  references: z.array(InquiryAnswerReferenceSchema).max(5).optional(),
  // 0 = 使い方ガイドに根拠が無かった。ガイド拡充の直接のネタになる
  referenceCount: z.number().int().min(0).optional(),
  status: z.enum(["answered", "error"]).optional(),
  // 0 = その問い合わせの 1 問目。週次の「AIへの問い合わせ件数」はこれを数える
  // （リクエストの history が空＝新しい問い合わせの始まり）
  turnIndex: z.number().int().min(0).optional(),
  model: z.string().max(100).optional(),
  // 将来 web が会話 ID を送るようになったとき用。現時点では書かない
  sessionId: z.string().max(64).optional(),

  // ---- kind === "handoff" ----
  inquiryId: z.string().max(64).optional(),
  qaTurnCount: z.number().int().min(0).optional(),
});
export type DDBInquiryQaLog = z.infer<typeof DDBInquiryQaLogSchema>;

export interface InquiryResponse {
  success: boolean;
  inquiry: InquiryView;
}

export interface InquiriesResponse {
  success: boolean;
  inquiries: InquiryView[];
}

// ==========================================
// 自由記録モード非同期処理 (Speed Recording Async)
// ==========================================

/**
 * 自由記録アップロードURL取得API
 * POST /speed-recording/upload-url
 * Meeting事前作成 + S3 presigned URL生成
 *
 * contentType / extension はクライアントブラウザが対応するMIMEに応じて指定。
 * Safari は audio/mp4 (拡張子 m4a)、それ以外は audio/webm (拡張子 webm)。
 * 後方互換のため default 付き optional とし、未指定時は webm 扱い。
 */
export const SpeedRecordingUploadUrlRequestSchema = z.object({
  presetId: z.string(),
  contentType: z
    .enum(["audio/webm", "audio/mp4"])
    .default("audio/webm")
    .optional(),
  extension: z.enum(["webm", "m4a"]).default("webm").optional(),
  groupId: z.string().nullable().optional(),
  // PRODUCT_DEV-403: 続きの商談 × 自由記録モードで親商談から社名・担当者を引き継ぐ
  sourceMeetingId: z.string().optional(),
  company: z.string().nullable().optional(),
  contacts: z.array(ContactSchema).nullable().optional(),
  // PRODUCT_DEV-655(第2弾): 顧客詳細（個人）から自由記録を始めたときに、顧客マスタの
  // お客様情報を extractedData.customer_info の初期値として引き継ぐ（JSON 文字列）。
  customerInfo: z.string().nullable().optional(),
});

export type SpeedRecordingUploadUrlRequest = z.infer<
  typeof SpeedRecordingUploadUrlRequestSchema
>;

export interface SpeedRecordingUploadUrlResponse {
  success: boolean;
  meetingId: string;
  uploadUrl: string;
  s3Key: string;
}

/**
 * 自由記録サブミットAPI
 * POST /speed-recording/submit
 * SQSキューへの非同期処理投入
 */
export const SpeedRecordingSubmitRequestSchema = z.object({
  meetingId: z.string(),
  // PRODUCT_DEV-485: クライアント計測の録音秒数。gpt-4o-transcribe は duration を
  // 返さないため、収録時間はフロント計測値を採用する（未指定時は worker 側で 0 にフォールバック）。
  durationSec: z.number().int().nonnegative().optional(),
});

export type SpeedRecordingSubmitRequest = z.infer<
  typeof SpeedRecordingSubmitRequestSchema
>;

export interface SpeedRecordingSubmitResponse {
  success: boolean;
  meetingId: string;
  status: "processing";
}

/**
 * 対話モード音声アップロードURL取得API（PRODUCT_DEV-514）
 * POST /recording/audio-upload-url
 * 既存 Meeting（dialogue モード、会話末で作成済み）に紐づく音声を
 * 故障解析用に S3 へ保存するための presigned PUT URL を発行する。
 */
export const RecordingAudioUploadUrlRequestSchema = z.object({
  meetingId: z.string(),
  contentType: z
    .enum(["audio/webm", "audio/mp4"])
    .default("audio/webm")
    .optional(),
  extension: z.enum(["webm", "m4a"]).default("webm").optional(),
});

export type RecordingAudioUploadUrlRequest = z.infer<
  typeof RecordingAudioUploadUrlRequestSchema
>;

export interface RecordingAudioUploadUrlResponse {
  success: boolean;
  uploadUrl: string;
  s3Key: string;
}

/**
 * SQSメッセージ型（speed-recording-worker用）
 *
 * contentType は worker が Whisper API へ渡すファイル種別の判定に使用。
 * 後方互換: 未指定/undefinedの場合は audio/webm として扱う。
 */
export interface SpeedRecordingProcessorMessage {
  tenantId: string;
  userId: string;
  meetingId: string;
  presetId: string;
  s3Key: string;
  contentType?: string;
  // PRODUCT_DEV-485: クライアント計測の録音秒数。worker が収録時間に採用（未指定は 0）。
  durationSec?: number;
}

/**
 * SQSメッセージ型（dialogue-completion-worker用 / PRODUCT_DEV-525）
 *
 * 対話モードの GateKeeper 補完を非同期化するキュー。producer は meetings/post.ts、
 * consumer は dialogue-completion/worker。presetId は worker が preset 駆動で
 * extractedData を検証するため使用（未指定可）。
 */
export interface DialogueCompletionMessage {
  tenantId: string;
  userId: string;
  meetingId: string;
  conversationLog: string;
  presetId?: string;
  /**
   * PRODUCT_DEV-612（連鎖モデル）: 起票元。既定（未指定）は新規商談の作成。
   *
   * `"review"` は既存商談への振り返り追記（producer は meetings/[meetingId]/patch.ts）。
   * worker はこのとき振り返り項目の抽出指示（ONP `MINUTES_REVIEW_EXTRACTION`）を preset へ足す。
   *
   * `"minutes"` は議事録本文から作った商談（producer は meetings/post.ts）。
   * worker はこのとき抽出テンプレートへ箇条書き書式ルールを連結する
   * （議事録は材料が長く、散文だと項目が読めなくなるため）。
   */
  mode?: "review" | "minutes";
}

// ============================================================
// PRODUCT_DEV-424 メモ / ToDo 機能
// ============================================================

/**
 * AI 抽出 ToDo 候補（Memo に embed、ユーザ採択時のみ UnizoTodos に作成）
 */
export const SuggestedTodoSchema = z.object({
  title: z.string().min(1).max(200),
  dueAt: z.string().nullable().optional(), // ISO8601、null=期限なし
  confidence: z.number().min(0).max(1).optional(), // AI 信頼度
});
export type SuggestedTodo = z.infer<typeof SuggestedTodoSchema>;

/**
 * DynamoDB メモ型
 * PK: TENANT#{tenantId}#USER#{userId}（Q1案A: 物理強制）
 * SK: MEMO#{createdAt}#{memoId}（新着降順 Query）
 */
export interface DDBMemo {
  PK: string; // TENANT#{tenantId}#USER#{userId}
  SK: string; // MEMO#{createdAt}#{memoId}
  GSI1PK: string; // TENANT#{tenantId}#USER#{userId}
  GSI1SK: string; // MEMO#{memoId} （1件取得用）
  memoId: string;
  tenantId: string;
  userId: string;
  type: "voice" | "manual";
  content: string;
  title?: string; // Phase 5: メモタイトル (worker が AI 生成 or user が手動編集)
  audioS3Key?: string;
  durationSec?: number; // D3: 音声の長さ(秒)。voice メモのみ、worker が保存
  transcriptStatus?: "pending" | "transcribing" | "extracting" | "ready" | "failed";
  suggestedTodos?: SuggestedTodo[]; // AI 抽出候補（Q2案A: Memo 同梱）
  companyId?: string; // Phase 4 で利用
  meetingId?: string; // Phase 4 で利用
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  status?: "active" | "trashed";
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number;
}

// メモ作成リクエスト
export const CreateMemoSchema = z.object({
  type: z.enum(["voice", "manual"]).default("manual"),
  content: z.string().min(1, "メモ内容は必須です").max(5000, "メモは5000文字以内です"),
  title: z.string().max(120).optional(), // Phase 5
  companyId: z.string().optional(),
  meetingId: z.string().optional(),
});
export type CreateMemoRequest = z.infer<typeof CreateMemoSchema>;

// メモ更新リクエスト
export const UpdateMemoSchema = z.object({
  content: z.string().min(1).max(5000).optional(),
  title: z.string().max(120).nullable().optional(), // Phase 5
  companyId: z.string().nullable().optional(),
  meetingId: z.string().nullable().optional(),
  // Phase 7 #12/#13: AI 候補(suggestedTodos)の採択/却下/差し戻しを永続化するため
  // フロントから配列ごと差し替えできるようにする (後方互換 optional)
  suggestedTodos: z.array(SuggestedTodoSchema).optional(),
});
export type UpdateMemoRequest = z.infer<typeof UpdateMemoSchema>;

// メモ単体レスポンス用のサブ型
export interface MemoItem {
  memoId: string;
  type: "voice" | "manual";
  content: string;
  title?: string; // Phase 5
  audioS3Key?: string;
  durationSec?: number; // D3
  transcriptStatus?: DDBMemo["transcriptStatus"];
  suggestedTodos?: SuggestedTodo[];
  companyId?: string;
  meetingId?: string;
  createdAt: string;
  updatedAt: string;
  // PRODUCT_DEV-506: 登録者の userId。テナント管理者向けレスポンスでのみ付与され、
  // 一般ユーザー向けレスポンスでは省略される（プライバシー）。フロントは users マスタで名前解決。
  createdBy?: string;
}

export interface MemosResponse {
  success: boolean;
  memos: MemoItem[];
}

export interface MemoResponse {
  success: boolean;
  memo: MemoItem;
}

export interface DeleteMemoResponse {
  success: boolean;
  memoId: string;
}

/**
 * DynamoDB ToDo 型
 * PK: TENANT#{tenantId}#USER#{userId}
 * SK: TODO#{dueAt|9999}#{todoId}（期限昇順 Query、期限なしは末尾）
 */
export interface DDBTodo {
  PK: string;
  SK: string;
  GSI1PK: string; // TENANT#{tenantId}#USER#{userId}
  GSI1SK: string; // TODO#{todoId} （1件取得用）
  todoId: string;
  tenantId: string;
  userId: string;
  title: string;
  dueAt?: string; // ISO8601、未設定=期限なし
  note?: string; // 補足メモ (D7)
  status: "pending" | "done";
  sourceMemoId?: string;
  // PRODUCT_DEV-586: 議事録の Nextアクションから「落とした」ToDo の逆引き用（単一・任意）。
  sourceMinutesId?: string;
  // PRODUCT_DEV-586: 採択元 Nextアクション行の id。議事録詳細の「ToDo化済み」判定に使う（任意）。
  sourceActionId?: string;
  sourceType: "manual" | "ai_extracted";
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number;
}

// ToDo 作成リクエスト
export const CreateTodoSchema = z.object({
  title: z.string().min(1, "タイトルは必須です").max(200, "タイトルは200文字以内です"),
  dueAt: z.string().nullable().optional(),
  note: z.string().max(2000, "補足は2000文字以内です").optional(),
  sourceMemoId: z.string().optional(),
  // PRODUCT_DEV-586: 議事録詳細から ToDo を作成する場合の紐づけ（任意・後方互換）。
  sourceMinutesId: z.string().optional(),
  // PRODUCT_DEV-586: 採択元 Nextアクション行の id（任意・後方互換）。
  sourceActionId: z.string().optional(),
  sourceType: z.enum(["manual", "ai_extracted"]).default("manual"),
});
export type CreateTodoRequest = z.infer<typeof CreateTodoSchema>;

// ToDo 一括作成（AI 抽出候補の採択用、Phase 3）
export const BatchCreateTodosSchema = z.object({
  sourceMemoId: z.string(),
  todos: z.array(CreateTodoSchema.omit({ sourceType: true, sourceMemoId: true })).min(1).max(50),
});
export type BatchCreateTodosRequest = z.infer<typeof BatchCreateTodosSchema>;

// ToDo 更新リクエスト
export const UpdateTodoSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  dueAt: z.string().nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
  status: z.enum(["pending", "done"]).optional(),
});
export type UpdateTodoRequest = z.infer<typeof UpdateTodoSchema>;

// ToDo レスポンス用サブ型
export interface TodoItem {
  todoId: string;
  title: string;
  dueAt?: string;
  note?: string;
  status: "pending" | "done";
  sourceMemoId?: string;
  // PRODUCT_DEV-586: 議事録の Nextアクションから作成された ToDo の逆引き用（単一・任意）。
  sourceMinutesId?: string;
  // PRODUCT_DEV-586: 採択元 Nextアクション行の id。議事録詳細の「ToDo化済み」判定に使う（任意）。
  sourceActionId?: string;
  sourceType: "manual" | "ai_extracted";
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  // PRODUCT_DEV-506: 登録者の userId。テナント管理者向けレスポンスでのみ付与される（プライバシー）。
  createdBy?: string;
}

export interface TodosResponse {
  success: boolean;
  todos: TodoItem[];
}

export interface TodoResponse {
  success: boolean;
  todo: TodoItem;
}

export interface DeleteTodoResponse {
  success: boolean;
  todoId: string;
}

// ============================================================
// PRODUCT_DEV-424 Phase 2: 音声メモ（録音 + 文字起こし）
// ============================================================

/**
 * POST /memos/audio-upload-url リクエスト
 * クライアント側で録音した音声の MIME を指定。
 */
export const MemoAudioUploadUrlSchema = z.object({
  contentType: z.string().optional(), // 例: "audio/webm", "audio/mp4"
  extension: z.string().optional(), // 例: "webm", "m4a"
});
export type MemoAudioUploadUrlRequest = z.infer<typeof MemoAudioUploadUrlSchema>;

export interface MemoAudioUploadUrlResponse {
  success: boolean;
  memoId: string;
  uploadUrl: string;
  s3Key: string;
}

export interface MemoTranscribeResponse {
  success: boolean;
  memoId: string;
  status: "transcribing";
}

/**
 * SQS メッセージ（memo-transcribe-worker 用）
 * speed-recording のメッセージとは別 Queue / 別 worker で処理する。
 */
export interface MemoTranscriptionProcessorMessage {
  tenantId: string;
  userId: string;
  memoId: string;
  s3Key: string;
  contentType?: string;
  // PRODUCT_DEV-459: 文字起こし起動時刻 (ISO)。worker で処理時間(elapsed)を算出し、
  // 一定時間以上かかった完成のみ通知するための閾値判定に使う。後方互換のため optional。
  enqueuedAt?: string;
  // PRODUCT_DEV-485: クライアント計測の録音秒数。worker が durationSec に採用（未指定は API duration）。
  durationSec?: number;
}

// ===========================================================================
// PRODUCT_DEV-583: マイ顧客 (My Customers)
// 顧客マスタ(DDBCompany)を「自分の担当顧客」として一覧/詳細/編集/引き継ぎ/ブックマーク/
// 録音起点プレフィルするための request/response 契約。すべて additive・後方互換
// (既存スキーマは非改変)。web(apps/unizo/web)がこれらを import する。
// 一覧アイテムの導出ロジックは apps/unizo/api/lib/myCustomers/derive.ts が正本で、
// 下記 MyCustomerListItemSchema はその構造を web 向けにミラーしたもの。
// ===========================================================================

// --- 共通 enum ---
export const MyCustomersScopeSchema = z.enum(["mine", "all"]);
export type MyCustomersScopeContract = z.infer<typeof MyCustomersScopeSchema>;
export const MyCustomersSortSchema = z.enum(["recent", "oldest", "dealCount"]);
export type MyCustomersSortContract = z.infer<typeof MyCustomersSortSchema>;

// --- tenant-users GET: サブ担当/引き継ぎ先ピッカー用のアクティブなテナントメンバー ---
export const TenantUserSummarySchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(["admin", "member", "viewer"]),
});
export type TenantUserSummary = z.infer<typeof TenantUserSummarySchema>;
export const TenantUsersResponseSchema = z.object({
  users: z.array(TenantUserSummarySchema),
});
export type TenantUsersResponse = z.infer<typeof TenantUsersResponseSchema>;

// --- PRODUCT_DEV-583(R3): マイ顧客メンバーシップ（per-user・商談ブックマーク型テーブル UnizoMyCustomers） ---
// PK=TENANT#{tid}#USER#{uid}, SK=CUSTOMER#{companyId}。1行が auto(reviewed 記録による自動登録) と
// bookmarked(手動保存) の両フラグを持つ。両方 false なら行は削除する（存在＝どちらか true）。
// 主担当(ownerId)/サブ担当(subAssigneeIds) は本テーブル導入により廃止（DDBCompany のフィールドは
// 後方互換のため残置・不使用）。「マイ顧客」= 自分の membership 行に存在する顧客。
export const DDBMyCustomerSchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // CUSTOMER#<companyId>
  tenantId: z.string(),
  userId: z.string(),
  companyId: z.string(),
  auto: z.boolean(), // reviewed 記録で自動登録された
  bookmarked: z.boolean(), // 手動で保存(ブックマーク)された
  companyName: z.string().optional(), // 非正規化スナップショット（一覧の軽量表示用）
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBMyCustomer = z.infer<typeof DDBMyCustomerSchema>;

// --- my-customers GET (一覧) ---
export const MyCustomerListItemSchema = z.object({
  companyId: z.string(),
  name: z.string(),
  nameKana: z.string(),
  type: z.enum(["corporate", "individual"]),
  sub: z.string(),
  ownerId: z.string().optional(),
  ownerName: z.string().optional(),
  subAssigneeIds: z.array(z.string()),
  // PRODUCT_DEV-583(R3): membership フラグ。auto=自分が記録した(自動登録) / bookmarked=手動保存。
  auto: z.boolean(),
  bookmarked: z.boolean(),
  dealCount: z.number(),
  lastMeetingAt: z.string().nullable(),
  lastMeetingBy: z.string().optional(),
  lastMeetingByName: z.string().optional(),
  lastMeetingTitle: z.string().optional(),
  lastPresetId: z.string().optional(),
  lastRecordingMode: z.enum(["dialogue", "speed"]).optional(),
  isOwner: z.boolean(),
  // PRODUCT_DEV-606: 顧客(会社)の登録日時。「顧客登録順」ソート用（DDBCompany.createdAt 由来）。
  // 後方互換のため optional（既存の永続データ/消費者を壊さない）。
  createdAt: z.string().datetime().optional(),
});
export type MyCustomerListItemContract = z.infer<typeof MyCustomerListItemSchema>;
export const MyCustomerListResponseSchema = z.object({
  customers: z.array(MyCustomerListItemSchema),
});
export type MyCustomerListResponse = z.infer<typeof MyCustomerListResponseSchema>;

// --- my-customers/[companyId] GET (詳細) ---
export const MyCustomerDealSchema = z.object({
  id: z.string(),
  title: z.string(),
  date: z.string(), // "YYYY-MM-DD"
  by: z.string(), // 記録者 userId
  byName: z.string().optional(),
  presetId: z.string().optional(),
  reviewed: z.boolean(),
});
export type MyCustomerDeal = z.infer<typeof MyCustomerDealSchema>;
export const SubAssigneeSummarySchema = z.object({
  userId: z.string(),
  name: z.string(),
});
export type SubAssigneeSummary = z.infer<typeof SubAssigneeSummarySchema>;
export const MyCustomerDetailSchema = DDBCompanySchema.extend({
  dealCount: z.number(),
  deals: z.array(MyCustomerDealSchema),
  canEdit: z.boolean(),
  ownerName: z.string().optional(),
  subAssignees: z.array(SubAssigneeSummarySchema),
  // PRODUCT_DEV-583: 呼び出しユーザーがこの顧客をブックマーク済みか（詳細の初期表示用）。
  bookmarked: z.boolean(),
});
export type MyCustomerDetail = z.infer<typeof MyCustomerDetailSchema>;
export const MyCustomerDetailResponseSchema = z.object({
  customer: MyCustomerDetailSchema,
});
export type MyCustomerDetailResponse = z.infer<
  typeof MyCustomerDetailResponseSchema
>;

// --- my-customers/[companyId] PATCH (フル編集) ---
// PRODUCT_DEV-583(R3): 主担当/サブ担当は廃止。編集はフラグ有効テナントの認証ユーザーなら可。
// ownerId/subAssigneeIds は書き込ませない（許可フィールドのみのホワイトリスト）。
// useApiPatch が tenantId を注入するが非 strict schema が strip する。
export const UpdateMyCustomerRequestSchema = z.object({
  name: z.string().optional(),
  nameKana: z.string().optional(),
  aliases: z.array(z.string().min(1)).optional(),
  type: z.enum(["corporate", "individual"]).optional(),
  location: z.string().optional(),
  contacts: z.array(ContactSchema).optional(),
  systemIntegrationId: z.string().optional(),
  gbizCorporateNumber: z.string().optional(),
  individualProfile: IndividualProfileSchema.optional(),
});
export type UpdateMyCustomerRequest = z.infer<
  typeof UpdateMyCustomerRequestSchema
>;

// --- my-customers/[companyId]/sub-assignees POST ---
export const AddSubAssigneeRequestSchema = z.object({
  userId: z.string().min(1),
});
export type AddSubAssigneeRequest = z.infer<typeof AddSubAssigneeRequestSchema>;

// --- my-customers/[companyId]/owner POST (主担当の割り当て/付け替え) ---
// PRODUCT_DEV-583: 未所有(ownerId 未設定)なら任意の在籍メンバーを主担当に設定可、
// 所有済みは現主担当または admin のみ変更可（認可はハンドラで判定）。
export const AssignOwnerRequestSchema = z.object({
  ownerId: z.string().min(1),
});
export type AssignOwnerRequest = z.infer<typeof AssignOwnerRequestSchema>;

// --- my-customers/[companyId]/bookmark POST/DELETE ---
export const BookmarkResponseSchema = z.object({
  companyIds: z.array(z.string()),
});
export type BookmarkResponse = z.infer<typeof BookmarkResponseSchema>;

// --- my-customers/[companyId]/recording-context GET (録音起点プレフィル) ---
export const RecordingContextResponseSchema = z.object({
  company: z.object({
    companyId: z.string(),
    name: z.string(),
    type: z.enum(["corporate", "individual"]),
    contacts: z.array(ContactSchema),
    // PRODUCT_DEV-655(第2弾): 個人顧客の既知情報を録音開始時に引き継ぐための追加。
    // 顧客詳細から記録を始めたのに氏名から聞き直される問題の解消に使う（後方互換の optional）。
    nameKana: z.string().optional(),
    individualProfile: IndividualProfileSchema.optional(),
  }),
  lastPresetId: z.string().optional(),
  lastRecordingMode: z.enum(["dialogue", "speed"]).optional(),
});
export type RecordingContextResponse = z.infer<
  typeof RecordingContextResponseSchema
>;

// --- my-customers/handover POST (主担当の一括引き継ぎ) ---
export const HandoverRequestSchema = z.object({
  targetUserId: z.string().min(1),
  // 未指定なら「自分が主担当の全顧客」を対象にする。
  companyIds: z.array(z.string()).optional(),
  // true=引き継ぎ後も元主担当をサブ担当として残す。
  keepAsSub: z.boolean(),
});
export type HandoverRequest = z.infer<typeof HandoverRequestSchema>;
export const HandoverResponseSchema = z.object({
  transferred: z.array(z.string()), // 引き継いだ companyId
  skipped: z.array(z.string()), // 主担当不一致等でスキップした companyId
});
export type HandoverResponse = z.infer<typeof HandoverResponseSchema>;

// --- my-customers/today-copy GET (本日レビュー済み商談のコピー文集約) ---
export const TodayCopyResponseSchema = z.object({
  text: z.string(),
});
export type TodayCopyResponse = z.infer<typeof TodayCopyResponseSchema>;
// ============================================================
// PRODUCT_DEV-586 議事録（録音タブ / 長尺プログレッシブ文字起こし）
// ============================================================
// メモ(DDBMemo)・商談記録(DDBMeeting)とは別の独立レコード種別。
// 60分級の録音を無音境界チャンクで分割し、チャンク毎に文字起こし→順序結合→
// 議事録専用の固定フィールド構造化(AI要約/決定事項/Nextアクション)を 1 回だけ実行する。
// 全文(文字起こし)は S3 に保存し、DDB はポインタ(transcriptS3Key)のみ保持する。
// 議事録は全テナントに100%解放済み(PRODUCT_DEV-614 でフラグ撤去)。

/**
 * Nextアクション行（議事録から抽出 or 手動追加。ToDo へ採択可能）
 */
export const MinutesActionSchema = z.object({
  id: z.string(),
  text: z.string().min(1).max(1000),
  due: z.string().nullable().optional(), // ISO8601 or "YYYY/MM/DD"、null=期限なし
  owner: z.string().max(100).optional(), // 担当（任意）
  done: z.boolean().default(false),
});
export type MinutesAction = z.infer<typeof MinutesActionSchema>;

/** 議事録の処理ステータス（アップロード開始以降をサーバが管理） */
export type MinutesProcessStatus =
  | "uploading" // シェル作成〜チャンクアップロード中
  | "transcribing" // チャンク文字起こし進行中
  | "structuring" // 全文結合後の構造化(要約/決定事項/Next)実行中
  | "ready" // 完了
  | "failed";

/**
 * DynamoDB 議事録型
 * PK: TENANT#{tenantId}#USER#{userId}（メモ同様に per-user 物理強制）
 * SK: MINUTES#{createdAt}#{minutesId}（新着降順 Query）
 */
export interface DDBMeetingMinutes {
  PK: string; // TENANT#{tenantId}#USER#{userId}
  SK: string; // MINUTES#{createdAt}#{minutesId}
  GSI1PK: string; // TENANT#{tenantId}（owner 非依存。GSI_MinutesById）
  GSI1SK: string; // MINUTES#{minutesId} （1件取得用）
  /**
   * PRODUCT_DEV-612: GSI_MinutesByMeeting（商談→議事録の逆引き）。**sparse index**。
   * `meetingId` が設定されているアイテムにだけ書き、解除時は属性ごと REMOVE する
   * （空文字を入れるとゴミキーで索引に残るため）。
   */
  GSI2PK?: string; // TENANT#{tenantId}
  GSI2SK?: string; // MEETING#{meetingId}#{minutesId}
  minutesId: string;
  tenantId: string;
  userId: string;

  // --- メタ / 一覧表示 ---
  title: string; // AI 生成 or 手動編集
  date: string; // "YYYY-MM-DD"（会議日、ユーザ編集可）
  time?: string; // "HH:mm:ss"
  durationSec?: number; // 録音長（最大 ~3600）

  // --- 構造化フィールド（固定契約。preset 駆動ではない） ---
  summary?: string; // AI要約（録音 5 分以上で自動生成）
  decisions?: string[]; // 決定事項
  nextActions?: MinutesAction[]; // Nextアクション
  attendees?: string; // 出席者（任意・手動入力のフリーテキスト）
  companyId?: string; // 関連企業（企業マスタ紐付け、単一）
  meetingId?: string; // 関連商談（任意）

  // --- 全文ポインタ（S3、インライン保存しない） ---
  transcriptS3Key?: string; // minutes/{tenantId}/{minutesId}/transcript.txt
  transcriptChars?: number; // 全文の文字数（プレビュー/メトリクス用）
  // PRODUCT_DEV-603: クリーン化(Gatekeeper)実行時のみ保存する生(未クリーン)全文ポインタ。
  // minutes-raw/{tenantId}/{minutesId}/transcript.txt（S3で30日失効）。監査用・admin限定閲覧。
  // 未設定 = クリーン化していない（本文が生そのもの）→ 生データ導線は出さない。
  rawTranscriptS3Key?: string;

  // --- プログレッシブ・パイプライン進捗 ---
  processStatus: MinutesProcessStatus;
  expectedChunks?: number; // finalize 時に確定するチャンク総数
  chunksDone?: number; // 文字起こし済チャンク数（progress 用）
  failureReason?: string;
  // 録音中にクライアントが定期送信する生存signal（ISO8601）。これが途絶えた uploading は
  // 「クライアントが死んで finalize を呼べなかった」とみなし minutes-sweeper が回収する。
  heartbeatAt?: string;

  // --- 監査 / 論理削除（メモ同一規約） ---
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  version: number;
  status?: "active" | "trashed";
  deletedAt?: string;
  deletedBy?: string;
  ttl?: number; // trashed 時のみ epoch 秒（アクティブ時は未設定 = 永続）
}

/** 処理中（アップロード〜構造化）判定。card/list/detail で共有し状態集合の重複を防ぐ。 */
export const isMinutesInProgress = (s: MinutesProcessStatus): boolean =>
  s === "uploading" || s === "transcribing" || s === "structuring";

// 議事録更新（詳細画面での手動編集）
export const UpdateMinutesSchema = z.object({
  title: z.string().max(120).nullable().optional(),
  summary: z.string().max(20000).nullable().optional(),
  decisions: z.array(z.string().max(2000)).optional(),
  nextActions: z.array(MinutesActionSchema).optional(),
  attendees: z.string().max(2000).nullable().optional(),
  companyId: z.string().nullable().optional(),
  meetingId: z.string().nullable().optional(),
  date: z.string().optional(),
});
export type UpdateMinutesRequest = z.infer<typeof UpdateMinutesSchema>;

// 一覧アイテム（全文・構造化詳細は含めない軽量表現）
export interface MinutesListItem {
  minutesId: string;
  title: string;
  date: string;
  time?: string;
  durationSec?: number;
  processStatus: MinutesProcessStatus;
  summary?: string;
  companyId?: string;
  createdAt: string;
  updatedAt: string;
  // PRODUCT_DEV-506 と同様、管理者向けレスポンスでのみ付与（プライバシー）
  createdBy?: string;
  /** PRODUCT_DEV-612: 関連商談（未紐づけなら undefined）。 */
  meetingId?: string;
  /**
   * PRODUCT_DEV-612: 閲覧者がこの議事録の詳細（`/minutes/{id}`）を開けるか＝オーナー本人 or 管理者。
   * `GET /minutes?meetingId=` は要約だけをテナント全員へ返すため、リンクの出し分けに使う。
   * 一覧 API（`GET /minutes`）では付与しない（そもそも自分の分しか返らない）。
   */
  canOpen?: boolean;
}

export interface MinutesListResponse {
  success: boolean;
  minutes: MinutesListItem[];
}

// 詳細（構造化フィールドを含む。全文は transcript-url 経由の署名 GET で別取得）
export interface MinutesDetail extends MinutesListItem {
  /**
   * 失敗の理由 (PRODUCT_DEV-805)。**`processStatus === "failed"` のときだけ入る。**
   *
   * 画面は「議事録の作成に失敗しました」と一律に出すのではなく、これがあればこれを出す。
   * 音声をほとんど拾えなかったのか、構造化で落ちたのかで、本人が次に取る行動が違う。
   */
  failureReason?: string;
  decisions?: string[];
  nextActions?: MinutesAction[];
  attendees?: string;
  meetingId?: string;
  transcriptAvailable: boolean; // transcriptS3Key の有無
  transcriptChars?: number;
  // PRODUCT_DEV-603: 生(未クリーン)全文が保全されているか（rawTranscriptS3Key の有無）。
  // true のときだけ admin 詳細に「生データ（未クリーン）」導線を表示する。
  rawTranscriptAvailable: boolean;
}

export interface MinutesResponse {
  success: boolean;
  minutes: MinutesDetail;
}

export interface DeleteMinutesResponse {
  success: boolean;
  minutesId: string;
}

// 録音開始のシェル作成レスポンス（body 無し。チャンクの署名PUTは chunk-upload-url で別取得）
export interface MinutesAudioUploadUrlResponse {
  success: boolean;
  minutesId: string;
}

// 録音音声のコンテナ種別（S3 キー拡張子・presign ContentType の許可値）
export const MinutesAudioExtensionSchema = z.enum(["webm", "m4a"]);
export const MinutesAudioContentTypeSchema = z.enum([
  "audio/webm",
  "audio/mp4",
]);

// チャンク毎の署名 PUT URL（seq = 0 始まりの連番）。extension/contentType は許可値に限定
// （S3 キーに素の文字列を差し込まないための入力バリデーション）。
export const MinutesChunkUploadUrlSchema = z.object({
  seq: z.number().int().min(0),
  contentType: MinutesAudioContentTypeSchema.optional(),
  extension: MinutesAudioExtensionSchema.optional(),
});
export type MinutesChunkUploadUrlRequest = z.infer<typeof MinutesChunkUploadUrlSchema>;

export interface MinutesChunkUploadUrlResponse {
  success: boolean;
  minutesId: string;
  seq: number;
  uploadUrl: string;
  s3Key: string;
}

// 録音終了: チャンク総数を確定し finalize（結合→構造化）を起動
// totalChunks 省略時はサーバが「その時点でアップロード済みのチャンク数」を採用する
// （離脱・中断で総数を数え切れなかったクライアントや、詳細画面の手動確定から呼ばれる）。
export const MinutesFinalizeSchema = z.object({
  totalChunks: z.number().int().min(0).optional(),
  durationSec: z.number().optional(),
  // 録音していたクライアント自身が離脱する（ページ破棄）ときの確定。生きている録音を
  // 他画面から誤って切り詰めないための生存ガードを、この宣言のときだけ免除する。
  abandoned: z.boolean().optional(),
  /**
   * 何も録れていない録音を**破棄する**宣言 (PRODUCT_DEV-787)。
   *
   * 議事録は録音しながら音声を分割送信するので、**開始した瞬間にレコードの器を作る**
   * （送り先の ID が要るため）。商談記録・音声メモは停止して送信するときに作るので、
   * 途中で離れても何も残らない。この差のせいで、議事録だけ「押しただけ」で
   * 空のレコードが残っていた。
   *
   * ⚠ **サーバは自分で条件を検証する。** クライアントが送ってきたからといって消さない
   * （まだ音声が 1 件も届いていない・finalize も走っていない、を必ず確かめる）。
   * 条件を満たさなければ通常の確定として扱い、**録れているものは決して消さない**。
   */
  discard: z.boolean().optional(),
});
export type MinutesFinalizeRequest = z.infer<typeof MinutesFinalizeSchema>;

export interface MinutesFinalizeResponse {
  success: boolean;
  minutesId: string;
  /** 破棄した場合は無い（レコードごと消えているため状態が存在しない）。 */
  processStatus?: MinutesProcessStatus;
  /**
   * 中身が無かったのでレコードごと消した (PRODUCT_DEV-787)。
   * 画面はこれが true のとき、詳細へ遷移せず一覧へ戻る（行き先が存在しない）。
   */
  discarded?: boolean;
}

// プログレッシブ表示: 録音中/処理中に afterSeq より後の新規チャンクのみ（デルタ）を返す。
// クライアントは text を順次追記し、次回は lastSeq を afterSeq として渡す（O(N²) 回避）。
export interface MinutesTranscriptProgressResponse {
  success: boolean;
  text: string; // afterSeq より後の新規チャンクを順序結合したデルタ
  lastSeq: number; // このレスポンスに含まれる最大 seq（無ければ要求時の afterSeq）
  completedChunks: number; // これまでに文字起こし済のチャンク総数
  processStatus: MinutesProcessStatus;
}

// 完了後の全文取得: S3 の署名 GET URL を返す（ブラウザが直接取得）
export interface MinutesTranscriptUrlResponse {
  success: boolean;
  url: string;
  expiresIn: number;
}

/**
 * SQS メッセージ（minutes-finalize-worker 用）
 * チャンク文字起こしは S3 ObjectCreated → SQS の S3 イベント通知で起動するため
 * 専用メッセージ型は持たない。finalize のみ本メッセージで起動する。
 */
export interface MinutesFinalizeMessage {
  tenantId: string;
  userId: string;
  minutesId: string;
  enqueuedAt?: string;
}

// ─── 活動サマリ / 個人・テナント設定 (PRODUCT_DEV-673) ───────────────────────
//
// テーブル: UnizoPreferences（プロダクト×テナント×個人の設定）
//   個人     PK: PRODUCT#{productId}#TENANT#{tenantId}#USER#{userId} / SK: PREF#{key}
//   テナント PK: PRODUCT#{productId}#TENANT#{tenantId}               / SK: TENANTPREF#{key}
//
// 値の解決順は **個人 → テナント既定 → コード既定**。要件の「管理者が設定した値が
// 初期値として配られる」がこの順序で成立する（useTenantFeatureFlag と同じ考え方）。
// PK に productId を含めるのは AgentJobsRepository と同じ作法で、将来 shared-domain へ
// 持ち上げるときにデータ移行を要らなくするため。
//
// 集計の純ロジック（期間解決・集計・推移）は ./activitySummary.ts を正とし、
// ここには API の入出力とDDBアイテムの形だけを置く。

/** 管理者が最後に見ていた UI。個人設定として記録し、次回そこから開く。 */
export const UiModeSchema = z.enum(["personal", "manager"]);
export type UiMode = z.infer<typeof UiModeSchema>;

/**
 * 個人設定の SK 名前空間。キー文字列はここを唯一の正とする。
 *
 * PRODUCT_DEV-698: 日報・週報・月報の配信設定は**個人設定ではなくなった**
 * （`SUMMARY_MAIL_*` を撤去）。誰にいつ出させるかは部の運用であって本人の好みではない、
 * というのが 2026-08-21 の判断。いまここに残るのは `uiMode` だけ。
 * ⚠ 既に保存済みの `PREF#SUMMARY_MAIL_*` アイテムは**消していない**（読まれなくなるだけ）。
 */
export const USER_PREFERENCE_KEYS = {
  uiMode: "UI_MODE",
  // PRODUCT_DEV-699: localStorage に逃がしていた個人設定をサーバへ移す。
  // 端末をまたいで引き継がれず、iOS Safari の ITP（7日間未アクセスで自動削除）等で
  // 黙って消えていた。localStorage は**下書き（キャッシュ）**として残し、正はこちら。
  lastPresetId: "LAST_PRESET_ID",
  presetChosen: "PRESET_CHOSEN",
  recordMode: "RECORD_MODE",
  dockCollapsed: "DOCK_COLLAPSED",
  notificationViewedAt: "NOTIFICATION_VIEWED_AT",
  lastSeenReleaseVersion: "LAST_SEEN_RELEASE_VERSION",
  schedulingHintDismissed: "SCHEDULING_HINT_DISMISSED",
  aiVoiceVolume: "AI_VOICE_VOLUME",
  searchHistory: "SEARCH_HISTORY",
} as const;

/** 日報・週報・月報の3種。表示順もこの並び。 */
export const REPORT_KINDS = ["daily", "weekly", "monthly"] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/** 画面にもメールの件名にも出る呼び名。api と web で綴り直さない。 */
export const REPORT_LABEL: Record<ReportKind, string> = {
  daily: "日報",
  weekly: "週報",
  monthly: "月報",
};

/**
 * 配信設定の SK 名前空間（PRODUCT_DEV-698）。
 *
 * **テナントと部署で同じ文字列を使う。** SK の名前空間が `TENANTPREF#` と `DEPTPREF#` で
 * 分かれているので衝突しないし、同じキーにしておくと解決側が「部署 → テナント」を
 * 1つのキーで順に引ける（キーが2種あると、片方だけ綴り間違えても型が通ってしまう）。
 */
export const REPORT_PREFERENCE_KEYS = {
  daily: "REPORT_DAILY",
  weekly: "REPORT_WEEKLY",
  monthly: "REPORT_MONTHLY",
} as const satisfies Record<ReportKind, string>;

/** テナント設定の SK 名前空間。 */
export const TENANT_PREFERENCE_KEYS = {
  fiscalMonthCloseDay: "FISCAL_MONTH_CLOSE_DAY",
  fiscalWeekCloseDay: "FISCAL_WEEK_CLOSE_DAY",
  weeklyDueOffsetDays: "WEEKLY_DUE_OFFSET_DAYS",
  monthlyDueOffsetDays: "MONTHLY_DUE_OFFSET_DAYS",
  submitDueTime: "SUBMIT_DUE_TIME",
  ...REPORT_PREFERENCE_KEYS,
} as const;

/** 部署設定の SK 名前空間。いまのところ配信設定だけ。 */
export const DEPT_PREFERENCE_KEYS = {
  ...REPORT_PREFERENCE_KEYS,
} as const;

/**
 * 設定1件の DDB アイテム。
 * value は設定ごとに形が違う（文字列 / 数値 / 曜日と時刻のオブジェクト）ため
 * unknown で受け、キーごとのスキーマで読み出し時に検証する。
 */
export const DDBPreferenceSchema = z.object({
  PK: z.string(),
  SK: z.string(),
  productId: z.string(),
  tenantId: z.string(),
  userId: z.string().optional(), // テナント/部署スコープには無い
  deptId: z.string().optional(), // 部署スコープにだけ入る
  key: z.string(),
  value: z.unknown(),
  updatedAt: z.string(),
  updatedBy: z.string().optional(),
});
export type DDBPreference = z.infer<typeof DDBPreferenceSchema>;

/** 配信の時刻（JST・"HH:mm"）。 */
export const ReportTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/**
 * 日報・週報・月報の配信設定（PRODUCT_DEV-673 → PRODUCT_DEV-698 でスコープを移動）。
 *
 * 日報は平日のみ、月報は「締め日の翌営業日」に送るので**曜日を持たない**。
 * 週報だけ `weekday`（0=日 〜 6=土）を持つ。
 *
 * ⚠ **メール配信そのものはまだ実装されていない。** ここに保存された値を読んで送る仕組みは
 * 後続。設定だけ先に受け付けているので、画面には「まだ届かない」と明示すること。
 */
export const ReportScheduleSchema = z.object({
  enabled: z.boolean(),
  time: ReportTimeSchema,
  /** 週報のみ。0=日 〜 6=土。 */
  weekday: z.number().int().min(0).max(6).optional(),
});
export type ReportSchedule = z.infer<typeof ReportScheduleSchema>;

/**
 * 3種まとめた形。**null は「この層では決めていない」**という意味で、
 * 「送らない」（`enabled: false`）とは別物。取り違えると、部署で解除したつもりが
 * 「送らない」で固定されてテナント既定に戻せなくなる。
 */
export const ReportSchedulesSchema = z.object({
  daily: ReportScheduleSchema.nullable(),
  weekly: ReportScheduleSchema.nullable(),
  monthly: ReportScheduleSchema.nullable(),
});
export type ReportSchedules = z.infer<typeof ReportSchedulesSchema>;

/** PATCH 用。省略したキーは変更しない。null 指定で解除（＝上位の層に従う）。 */
export const PatchReportSchedulesSchema = z
  .object({
    daily: ReportScheduleSchema.nullable().optional(),
    weekly: ReportScheduleSchema.nullable().optional(),
    monthly: ReportScheduleSchema.nullable().optional(),
  })
  .strict();
export type PatchReportSchedules = z.infer<typeof PatchReportSchedulesSchema>;

/**
 * どの層も決めていなかったときの既定（PRODUCT_DEV-698）。
 *
 * 値はプロトタイプの表示（平日 18:30 / 月曜 8:00 / 締め翌営業日 8:00）に合わせてある。
 * **api と web の両方が要るのでここに置く。** 片方に置くと必ずもう片方で綴り直され、
 * 「画面には 18:30 と出るのに 18:00 に届く」というずれ方をする。
 */
export const DEFAULT_REPORT_SCHEDULES: Record<ReportKind, ReportSchedule> = {
  daily: { enabled: true, time: "18:30" },
  weekly: { enabled: true, time: "08:00", weekday: 1 },
  monthly: { enabled: true, time: "08:00" },
};

/*
 * PRODUCT_DEV-699: 個人設定の値スキーマ
 * ------------------------------------
 * 端末ローカル（localStorage）にしか無かったものをサーバへ移すぶん。
 * **値の形はここを唯一の正とする**（api の resolve と web の読み書きが同じ物を見る）。
 */

/**
 * 記録モード。**`"dialogue"` を `"dialog"` に詰めないこと。**
 * この語彙は商談レコードの `recordingMode` と `lastRecordingMode`（顧客ごとの前回）で
 * 既に永続化されている。設定側だけ綴りを変えると、同じ概念に2つの語彙ができる。
 * 記録ドック内部の `"dialog"` のほうが後発の孤立した綴りなので、そちらを寄せる。
 */
export const RecordModeSchema = z.enum(["dialogue", "speed"]);
export type RecordModePreference = z.infer<typeof RecordModeSchema>;

/** AI 音声出力の音量。0..1（`@shared-ui/audio/realtimeAudioOutput` の VOLUME_MIN/MAX と同じ）。 */
export const AiVoiceVolumeSchema = z.number().min(0).max(1);

/** 検索履歴の絞り込み条件。web 側の `GroupFilter` / `ReviewedFilter` と同じ語彙。 */
export const SearchHistoryItemSchema = z.object({
  id: z.string(),
  searchTerm: z.string(),
  filterPeriod: z.enum(["all", "today", "week", "month", "custom"]),
  filterDateRange: z.object({ from: z.string(), to: z.string() }).optional(),
  selectedCompany: z.string(),
  groupFilter: z.enum(["all", "grouped", "ungrouped"]),
  reviewedFilter: z.enum(["all", "unreviewed", "reviewed"]),
  isPinned: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type SearchHistoryItemContract = z.infer<typeof SearchHistoryItemSchema>;

/** 検索履歴の保持上限。web 側の MAX_ITEMS と同じ値をここから配る。 */
export const SEARCH_HISTORY_MAX_ITEMS = 10;

/**
 * 検索履歴。**配列は丸ごと置換**する（差分マージにすると、デバウンス中の
 * 連続操作＝追加してすぐピン留め、で片方を取りこぼす）。
 */
export const SearchHistorySchema = z
  .array(SearchHistoryItemSchema)
  .max(SEARCH_HISTORY_MAX_ITEMS);

/** GET /user-preferences。未設定は null（呼び出し側がフォールバックを決める）。 */
export const UserPreferencesSchema = z.object({
  uiMode: UiModeSchema.nullable(),
  // PRODUCT_DEV-699
  lastPresetId: z.string().nullable(),
  presetChosen: z.boolean().nullable(),
  recordMode: RecordModeSchema.nullable(),
  dockCollapsed: z.boolean().nullable(),
  notificationViewedAt: z.string().nullable(),
  lastSeenReleaseVersion: z.string().nullable(),
  schedulingHintDismissed: z.boolean().nullable(),
  aiVoiceVolume: AiVoiceVolumeSchema.nullable(),
  searchHistory: SearchHistorySchema.nullable(),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

/** PATCH /user-preferences。省略したキーは変更しない。null 指定で設定解除。 */
export const PatchUserPreferencesSchema = z
  .object({
    uiMode: UiModeSchema.nullable().optional(),
    // PRODUCT_DEV-699
    lastPresetId: z.string().min(1).nullable().optional(),
    presetChosen: z.boolean().nullable().optional(),
    recordMode: RecordModeSchema.nullable().optional(),
    dockCollapsed: z.boolean().nullable().optional(),
    notificationViewedAt: z.string().datetime().nullable().optional(),
    lastSeenReleaseVersion: z.string().min(1).nullable().optional(),
    schedulingHintDismissed: z.boolean().nullable().optional(),
    aiVoiceVolume: AiVoiceVolumeSchema.nullable().optional(),
    searchHistory: SearchHistorySchema.nullable().optional(),
  })
  .strict();
export type PatchUserPreferences = z.infer<typeof PatchUserPreferencesSchema>;

/**
 * 週の締め曜日。0=日曜 〜 6=土曜（`Date#getUTCDay()` と同じ並び）。
 *
 * 週は**この曜日で終わる7日間**。既定の日曜なら月〜日になり、これまでの挙動と一致する。
 */
export const FiscalWeekCloseDaySchema = z.number().int().min(0).max(6);

/** 提出期限の時刻（JST・"HH:mm"）。 */
export const SubmitDueTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/**
 * GET /admin/tenant-preferences。
 *
 * **締め日と提出期限は別物**。「19日締め・22日提出」のように、締めてから提出までに
 * 猶予を置く運用があるため、期限は締め日からの日数（オフセット）で持つ。
 * 締め日を動かしても猶予の日数はそのままでよい、という関係になる。
 *
 * - `fiscalMonthCloseDay` … 営業月の締め日（1〜31）。null はカレンダー月
 * - `fiscalWeekCloseDay` … 週の締め曜日（0=日 〜 6=土）。null は日曜（月〜日）
 * - `weeklyDueOffsetDays` / `monthlyDueOffsetDays` … 締め日の何日後が提出期限か
 * - `submitDueTime` … 期限の時刻（JST）。日報・週報・月報で共通
 */
export const TenantPreferencesSchema = z.object({
  fiscalMonthCloseDay: z.number().int().min(1).max(31).nullable(),
  fiscalWeekCloseDay: FiscalWeekCloseDaySchema.nullable(),
  weeklyDueOffsetDays: z.number().int().min(0).max(14).nullable(),
  monthlyDueOffsetDays: z.number().int().min(0).max(31).nullable(),
  submitDueTime: SubmitDueTimeSchema.nullable(),
  /**
   * PRODUCT_DEV-698: 日報・週報・月報の配信。**テナントの既定**で、
   * 部署がこれを上書きできる。null は「決めていない」＝ `DEFAULT_REPORT_SCHEDULES`。
   */
  reports: ReportSchedulesSchema,
});
export type TenantPreferences = z.infer<typeof TenantPreferencesSchema>;

export const PatchTenantPreferencesSchema = z
  .object({
    fiscalMonthCloseDay: z.number().int().min(1).max(31).nullable().optional(),
    fiscalWeekCloseDay: FiscalWeekCloseDaySchema.nullable().optional(),
    weeklyDueOffsetDays: z.number().int().min(0).max(14).nullable().optional(),
    monthlyDueOffsetDays: z.number().int().min(0).max(31).nullable().optional(),
    submitDueTime: SubmitDueTimeSchema.nullable().optional(),
    reports: PatchReportSchedulesSchema.optional(),
  })
  .strict();
export type PatchTenantPreferences = z.infer<typeof PatchTenantPreferencesSchema>;

/**
 * 部署設定（PRODUCT_DEV-698）。**テナント設定の例外**として使う。
 *
 * 持てるのは配信設定だけ。締め日と提出期限を部署ごとに変えられるようにしないのは、
 * 期間の切り方が部署でずれると**同じ「今週」が人によって違う7日間**になり、
 * 部を跨いだ集計が意味を失うため。
 */
export const DepartmentPreferencesSchema = z.object({
  reports: ReportSchedulesSchema,
});
export type DepartmentPreferences = z.infer<typeof DepartmentPreferencesSchema>;

export const PatchDepartmentPreferencesSchema = z
  .object({
    reports: PatchReportSchedulesSchema.optional(),
  })
  .strict();
export type PatchDepartmentPreferences = z.infer<
  typeof PatchDepartmentPreferencesSchema
>;

export type UserPreferencesResponse = {
  success: boolean;
  preferences: UserPreferences;
}

export type TenantPreferencesResponse = {
  success: boolean;
  preferences: TenantPreferences;
}

/**
 * GET /admin/department-preferences — **全部署ぶんをまとめて返す**。
 * 画面が部署を切り替えるたびに取りに行かなくて済むようにする（部署は多くても数十）。
 * 設定を1つも持たない部署はキーごと入らない。
 */
export type DepartmentPreferencesResponse = {
  success: boolean;
  byDeptId: Record<string, DepartmentPreferences>;
}

/** PATCH /admin/department-preferences/{deptId} */
export type DepartmentPreferenceResponse = {
  success: boolean;
  deptId: string;
  preferences: DepartmentPreferences;
}

/** 活動サマリの期間。日/週/月は同じ画面のタブ切替。 */
export const SummaryPeriodSchema = z.enum(["daily", "weekly", "monthly"]);
export type SummaryPeriodKind = z.infer<typeof SummaryPeriodSchema>;

/**
 * me=自分ぶん / team=部下ぶん。
 * **team と「userId を明示して他人を指す」場合の認可は middleware ではなく
 * `apps/unizo/api/lambda/activity-summary/get.ts` の baseHandler 内で 403 を返す**
 * （同じ Lambda で自分の集計も返すため、router 単位の needsAdminRole では分けられない）。
 * ここを「middleware で担保済み」と誤読してハンドラ内の判定を消さないこと。
 */
export const ActivitySummaryScopeSchema = z.enum(["me", "team"]);
export type ActivitySummaryScope = z.infer<typeof ActivitySummaryScopeSchema>;

/** 期間中に面談した顧客の1社ぶん。「今週会った顧客（9社）」のチップに使う。 */
export interface ActivitySummaryCompany {
  companyId: string;
  companyName: string;
  recordCount: number;
  lastMeetingOn: string; // "YYYY-MM-DD"
  /** この顧客と面談したメンバー（scope=team のときだけ入る）。 */
  members?: Array<{ userId: string; userName: string; recordCount: number }>;
  /** 直近の記録（scope=team のときだけ入る・新しい順・最大5件）。 */
  recentMeetings?: Array<{
    meetingId: string;
    title: string | null;
    occurredOn: string;
    userName: string;
  }>;
}

/**
 * 分類（プリセット）別の記録件数（PRODUCT_DEV-673 PR8・scope=team）。
 * 表示名は web が root loader の presets から解決する（削除済みプリセットで
 * 記録された過去の商談も名前が引けるよう、ここでは ID だけを返す）。
 */
export interface ActivityCategoryCount {
  /** プリセット未設定の記録は null。 */
  presetId: string | null;
  recordCount: number;
}

/**
 * 接触が途絶えている会社（PRODUCT_DEV-673 PR8・scope=team）。
 * **集計期間ではなく「最後に記録された日」からの経過日数**で拾う
 * （売上に直結する見落としを拾うのが目的なので、期間で切ると意味が無い）。
 */
export interface StaleCompany {
  companyId: string;
  companyName: string;
  lastContactOn: string; // "YYYY-MM-DD"
  daysSince: number;
}

/** 期間より後に予定されている次回アポ1件。「来週の予定（5件）」に使う。 */
/**
 * 今日を予定日とする商談（PRODUCT_DEV-675）。
 *
 * 過去の商談記録に書かれた「次回アポ」が今日のもの。**マイページの「今日の予定」の材料**で、
 * カレンダー連携が無くても Unizo が自前で持っている情報だけで組める。
 *
 * `hasRecordToday` は「その相手について今日すでに記録があるか」。
 * 記録済みのものに「記録する」を出し続けると、押していいのか分からなくなる。
 */
export interface TodayAppointment {
  /** 元になった商談 ID。ここから「続きを記録」へ繋ぐ。 */
  sourceId: string;
  companyId: string | null;
  companyName: string | null;
  /** 元の商談のタイトル（次回アポの用件そのものではない点に注意）。 */
  title: string | null;
  /**
   * その相手について**対象日に**商談記録があるか。
   * ⚠ 名前に today が入っているが、判定するのは `date` で指定された日
   * （前後の日も見られるようにした PRODUCT_DEV-675 以降）。
   */
  hasRecordToday: boolean;
  /**
   * 対象日にこの相手で作られた商談記録の ID（PRODUCT_DEV-675 8/31）。
   *
   * ⚠ **`sourceId` と別物。** あちらは「次回アポ」が書かれていた**過去の**商談で、
   * こちらは**その予定に対して今日作られた**商談。画面はこれで「記録を開く」の
   * 行き先を決める（記録したのにボタンが「記録作成」のままなのを直すため）。
   * 記録が無ければ null。複数あれば最初の 1 件。
   */
  recordedMeetingId?: string | null;
}

export type TodayAppointmentsResponse = {
  success: boolean;
  /**
   * JST の今日（"YYYY-MM-DD"）。**`date` と同じとは限らない。**
   * 見出しを「今日の予定」「明日の予定」「8/30(土) の予定」と出し分けるのに使う。
   */
  today: string;
  /**
   * このレスポンスが対象とする日（"YYYY-MM-DD"）。省略時は `today` と同じ
   * （PRODUCT_DEV-675 より前のクライアントは `date` を送らないので常に今日）。
   */
  date?: string;
  appointments: TodayAppointment[];
};

export interface ActivitySummaryNextAppointment {
  sourceId: string;
  companyName: string | null;
  title: string | null;
  nextActionOn: string; // "YYYY-MM-DD"
}

/** scope=team の1メンバーぶん。 */
export interface ActivitySummaryMember {
  userId: string;
  userName: string;
  metrics: ActivityMetrics;
  comparison: MetricsComparison;
  /** 最終活動日。活動が無ければ null。 */
  lastActivityOn: string | null;
  /**
   * その期間の提出状態（PRODUCT_DEV-673 PR6）。
   * 管理者は「本人確認済」と「自動のみ」を区別して読むため、真偽値にまとめない。
   */
  submitStatus: SummarySubmitStatus;
  /** 晴れ／雨。上司の「目を通したいところ」の主要な拾い源。 */
  flag: SummaryFlag;
  /** 本人の所感（先頭のみ）。上司が開く前に当たりを付けるための抜粋。 */
  noteExcerpt: string | null;
}

export type ActivitySummaryResponse = {
  success: boolean;
  scope: ActivitySummaryScope;
  range: PeriodRange;
  metrics: ActivityMetrics;
  /** 1つ前の期間の集計。「先週比」の分母ではなく差分の相手。 */
  previous: ActivityMetrics;
  comparison: MetricsComparison;
  trend: ActivityTrend;
  companies: ActivitySummaryCompany[];
  nextAppointments: ActivitySummaryNextAppointment[];
  /** scope=team のときだけ入る。 */
  members?: ActivitySummaryMember[];
  /** 分類（プリセット）別の記録件数。scope=team のときだけ入る。 */
  categories?: ActivityCategoryCount[];
  /** 接触が途絶えている会社。scope=team のときだけ入る。 */
  staleCompanies?: StaleCompany[];
  /**
   * 集計した枠。scope=team のときだけ入る（PRODUCT_DEV-698）。
   *
   * `scope=team` は**閲覧者の部署**で切る。`deptId: null` は「未所属」枠、
   * `name` が空文字なら**部署を1つも作っていないテナント**（＝全員が1枠で、
   * 画面は従来どおりの見出しを出す）。
   */
  teamScope?: { deptId: string | null; name: string; memberCount: number };
  /** 部のまとめ（AI）。scope=team のときだけ入る。まだ生成されていなければ null。 */
  teamSummary?: TeamSummaryView | null;
  generatedAt: string;
}

// ─── 日報 / 週報 / 月報（提出物）(PRODUCT_DEV-673) ───────────────────────────
//
// テーブル: UnizoActivitySummaries
//   PK: `TENANT#{tenantId}#USER#{userId}`（サマリの対象者）
//   SK: `SUMMARY#{period}#{periodKey}` 例 `SUMMARY#weekly#2026-W29`
//
// 数値そのものは毎回 ActivitySource から集計し直す（このテーブルには保存しない）。
// ここに載るのは **人が触ったもの と AI が書いたもの**＝本文・所感・フラグ・提出状態・
// 上司コメント・ハイライトと、提出時点の指標スナップショット。
//
// **提出は2段階**（要件まとめ §5）。設定時刻に自動送信された状態が `auto`、本人が確認して
// 提出した状態が `confirmed`。上司はこの2つを区別して読むため、1つの真偽値にまとめない。

/** 提出状態。none=未作成 / auto=自動のみ / confirmed=本人確認済 */
export const SummarySubmitStatusSchema = z.enum(["none", "auto", "confirmed"]);
export type SummarySubmitStatus = z.infer<typeof SummarySubmitStatusSchema>;

/** 晴れ=手応えあり / 雨=相談したい。任意で、未選択は none（＝普通として提出される）。 */
export const SummaryFlagSchema = z.enum(["none", "sunny", "rain"]);
export type SummaryFlag = z.infer<typeof SummaryFlagSchema>;

/** AI 下書きの生成状態。SQS ワーカーが更新し、フロントは completed までポーリングする。 */
export const SummaryAiStatusSchema = z.enum([
  "idle",
  "generating",
  "completed",
  "failed",
]);
export type SummaryAiStatus = z.infer<typeof SummaryAiStatusSchema>;

/** 顧客ごとのハイライト1件（「サンプル商事 — 価格帯に懸念。決裁は8月上旬の役員会。」）。 */
export const SummaryHighlightSchema = z.object({
  companyName: z.string(),
  text: z.string().max(500),
  /** 出典の商談。UI から詳細へ飛ばすために持つ。 */
  meetingId: z.string().optional(),
});
export type SummaryHighlight = z.infer<typeof SummaryHighlightSchema>;

/**
 * 本文のもとになった記録1件（PRODUCT_DEV-673）
 *
 * **AI が書いた本文が出鱈目でないことを、本人と上司がその場で確かめられるようにする。**
 * サマリを作るときに実際に読んだ記録を、そのまま並べて出典として残す。
 *
 * 形の正本は `./activitySummary.ts` の `ActivityEvent` で、ここはそのうち
 * **画面から辿るのに要る分だけ**を保存用に写したもの。集計値（面談人数・次回アポ）は
 * `metricsSnapshot` が持っているので重ねて持たない。
 */
export const SummarySourceKindSchema: z.ZodType<ActivitySourceKind> = z.enum([
  "meeting",
  "customer",
  "minutes",
  "schedule",
]);

export const SummarySourceSchema = z.object({
  kind: SummarySourceKindSchema,
  /** 起点レコードの ID。画面はこれで詳細へ飛ぶ。 */
  sourceId: z.string(),
  /** 活動日（JST の "YYYY-MM-DD"）。 */
  occurredOn: z.string(),
  title: z.string().max(200).optional(),
  companyName: z.string().max(200).optional(),
});
export type SummarySource = z.infer<typeof SummarySourceSchema>;

/** 出典として保存する上限。超えたぶんは `sourceTotal` で件数だけ伝える。 */
export const SUMMARY_SOURCE_LIMIT = 100;

/** 上司からのコメント1件。やり取りは残す（消さない）。 */
export const SummaryCommentSchema = z.object({
  commentId: z.string(),
  authorId: z.string(),
  /** 投稿時点の表示名スナップショット（退職後も誰の発言か読める）。 */
  authorName: z.string(),
  body: z.string().min(1).max(2000),
  createdAt: z.string(),
});
export type SummaryComment = z.infer<typeof SummaryCommentSchema>;

/**
 * 集計値のスキーマ。**形の正本は `./activitySummary.ts` の `ActivityMetrics`** で、
 * ここは提出時スナップショットを DDB に保存/検証するための zod 表現。
 * `z.ZodType<ActivityMetrics>` と明示しているので、両者がズレると型エラーになる。
 */
export const ActivityMetricsSchema: z.ZodType<ActivityMetrics> = z.object({
  recordCount: z.number(),
  companyCount: z.number(),
  contactCount: z.number(),
  newCustomerCount: z.number(),
  nextAppointmentCount: z.number(),
  nextAppointmentFrom: z.string().nullable(),
  nextAppointmentTo: z.string().nullable(),
  draftCount: z.number(),
});

export const DDBActivitySummarySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#USER#<userId>
  SK: z.string(), // SUMMARY#<period>#<periodKey>
  tenantId: z.string(),
  userId: z.string(),
  period: SummaryPeriodSchema,
  periodKey: z.string(),
  /** 期間の初日・最終日（"YYYY-MM-DD"）。後から締め日設定が変わっても当時の範囲が読める。 */
  periodFrom: z.string(),
  periodTo: z.string(),

  /** AI が書いた本文。テキストとして本人が直せる。 */
  body: z.string().max(20000).nullable().optional(),
  /** 本人の所感（任意）。 */
  note: z.string().max(4000).nullable().optional(),
  flag: SummaryFlagSchema.optional(),

  submitStatus: SummarySubmitStatusSchema,
  /** 自動確定した時刻。 */
  autoConfirmedAt: z.string().nullable().optional(),
  /** 本人が確認して提出した時刻。再提出のたびに更新する。 */
  confirmedAt: z.string().nullable().optional(),

  comments: z.array(SummaryCommentSchema).optional(),
  highlights: z.array(SummaryHighlightSchema).optional(),

  /**
   * 本文のもとになった記録（PRODUCT_DEV-673）。生成時に実際に読んだものだけを入れる。
   * 表示上限 `SUMMARY_SOURCE_LIMIT` で切っているので、切ったかどうかは `sourceTotal` で判る。
   * **生成前・旧データは undefined**（空配列＝「参考にした記録が無い」と区別する）。
   */
  sources: z.array(SummarySourceSchema).max(SUMMARY_SOURCE_LIMIT).optional(),
  /** 参考にした記録の総数（`sources` を切る前の件数）。 */
  sourceTotal: z.number().optional(),

  aiStatus: SummaryAiStatusSchema.optional(),
  aiErrorMessage: z.string().nullable().optional(),
  aiGeneratedAt: z.string().nullable().optional(),

  /** 提出/生成時点の集計値。後から数え直しても当時の数字が読める。 */
  metricsSnapshot: ActivityMetricsSchema.optional(),

  version: z.number().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
});
export type DDBActivitySummary = z.infer<typeof DDBActivitySummarySchema>;

// ─── チームのまとめ（PRODUCT_DEV-698）────────────────────────────────────────
//
// 同じ `UnizoActivitySummaries` テーブルに **PK の名前空間だけ足して**載せる。
//   PK: `TENANT#{tenantId}#TEAM#{deptId ?? "UNASSIGNED"}`
//   SK: `SUMMARY#{period}#{periodKey}`（個人と同じ形）
//
// 個人の提出物とはフィールドが違う（提出状態・所感・上司コメントを持たない）ので、
// `DDBActivitySummary` を optional だらけにせず別スキーマにする。**上司が読む/聞くための
// 生成物**であって、誰かが提出するものではない。

/** 部署に属さない人をまとめる枠の ID。PK に使うので空文字にはしない。 */
export const UNASSIGNED_DEPT_KEY = "UNASSIGNED";

export const DDBTeamSummarySchema = z.object({
  PK: z.string(), // TENANT#<tenantId>#TEAM#<deptId|UNASSIGNED>
  SK: z.string(), // SUMMARY#<period>#<periodKey>
  tenantId: z.string(),
  /** null は「未所属」枠。 */
  deptId: z.string().nullable(),
  period: SummaryPeriodSchema,
  periodKey: z.string(),
  periodFrom: z.string(),
  periodTo: z.string(),

  /** AI が書いた部のまとめ。**誰も編集しない**（個人の日報と違い提出物ではない）。 */
  body: z.string().max(20000).nullable().optional(),
  /** 生成時点の対象人数。「6名分」の表示に使う。 */
  memberCount: z.number().optional(),

  aiStatus: SummaryAiStatusSchema.optional(),
  aiErrorMessage: z.string().nullable().optional(),
  aiGeneratedAt: z.string().nullable().optional(),

  /** 読み上げ音声（PRODUCT_DEV-698 PR6）。S3 キーと概算の再生時間。 */
  audioKey: z.string().nullable().optional(),
  audioDurationSec: z.number().nullable().optional(),

  /**
   * この期間のメールを送った時刻（PRODUCT_DEV-698）。
   *
   * **二重送信を止めるための印**。配信の cron は 15 分おきに回るので、これが無いと
   * 同じ期間のメールを何度も送る。書き込みは `attribute_not_exists(mailSentAt)` の
   * 条件付きで、**送る前に押さえる**（送ってから押さえると、送信後の失敗で二重送信になる）。
   */
  mailSentAt: z.string().nullable().optional(),

  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DDBTeamSummary = z.infer<typeof DDBTeamSummarySchema>;

/**
 * `GET /activity-summary?scope=team` に相乗りして返す（PRODUCT_DEV-698）。
 *
 * **専用ルートを作らない。** 管理者ビューは開いた時点でこの API を1回叩くので、
 * まとめのためにもう1往復させる理由が無い。
 */
export interface TeamSummaryView {
  body: string | null;
  aiStatus: SummaryAiStatus;
  generatedAt: string | null;
  memberCount: number | null;
  /** 音声があるか。実 URL は別途 audio-url で署名して取る（PR6）。 */
  hasAudio: boolean;
  audioDurationSec: number | null;
}

/** PATCH /activity-summary/{period}/{periodKey}。省略したキーは変更しない。 */
export const PatchActivitySummarySchema = z
  .object({
    body: z.string().max(20000).nullable().optional(),
    note: z.string().max(4000).nullable().optional(),
    flag: SummaryFlagSchema.optional(),
    /**
     * true で「本人確認済」として提出する（再提出も同じ）。
     * false は送らない ——「提出を取り消す」操作は要件に無い。
     */
    submit: z.literal(true).optional(),
  })
  .strict();
export type PatchActivitySummary = z.infer<typeof PatchActivitySummarySchema>;

/** POST /activity-summary/comments（上司のコメント投稿）。 */
export const PostSummaryCommentSchema = z
  .object({
    /** コメント先のサマリ対象者。 */
    userId: z.string().min(1),
    period: SummaryPeriodSchema,
    periodKey: z.string().min(1),
    body: z.string().min(1).max(2000),
  })
  .strict();
export type PostSummaryComment = z.infer<typeof PostSummaryCommentSchema>;

export type ActivitySummaryDetailResponse = {
  success: boolean;
  /** 未作成なら null（0件と未作成を取り違えないため）。 */
  summary: DDBActivitySummary | null;
  /** 現時点で集計し直した値。保存済みスナップショットとは別に返す。 */
  metrics: ActivityMetrics;
  range: PeriodRange;
  /**
   * 提出期限（週報・月報のみ。日報は自動確定の 18:30 が期限そのものなので null）。
   * 締め日そのものではなく、締め日 + テナント設定の猶予日数。
   */
  submitDue?: { dueOn: string; dueTime: string; label: string } | null;
};

/** アーカイブ一覧の1行（過去の日報・週報・月報）。 */
export type ActivitySummaryArchiveItem = {
  period: SummaryPeriodKind;
  periodKey: string;
  periodFrom: string;
  periodTo: string;
  submitStatus: SummarySubmitStatus;
  flag: SummaryFlag;
  recordCount: number;
  commentCount: number;
  /**
   * その期間の集計（保存済みのスナップショット）。
   *
   * TOP の締めカードが**その期間の数値**を出すために要る（PRODUCT_DEV-675 9/1）。
   * 以前は TOP が「今日の日報の集計」をそのまま月報カードへ渡しており、
   * 月初のように今日の記録が 0 件だと**4つとも 0**になって、詳細画面（23件）と
   * 食い違っていた。
   *
   * ⚠ **無い場合がある**（生成前・スナップショットを持たない古い提出物）。
   * 出せないときは 0 を並べず、数値の行そのものを出さないこと。
   */
  metrics?: ActivityMetrics;
  /** 提出期限（週報・月報のみ）。TOP の締めカードに出す。 */
  submitDue?: { dueOn: string; dueTime: string; label: string } | null;
  /**
   * 一覧に出す短い抜粋。**所感を優先し、無ければ AI 本文の冒頭**。
   * 一覧で「その日に何があったか」を思い出せるのが目的なので、本人が書いた所感の方が強い。
   */
  excerpt?: string;
  updatedAt: string;
};

export type ActivitySummaryArchiveResponse = {
  success: boolean;
  items: ActivitySummaryArchiveItem[];
};

/** SQS メッセージ（activity-summary-generator 用）。 */
export interface ActivitySummaryGenerateMessage {
  tenantId: string;
  /**
   * 個人の下書きの対象者。**scope="team" のときは入らない**ので optional
   * （必須のままにすると、部のまとめを積むのに意味の無い空文字を入れることになる）。
   */
  userId?: string;
  period: SummaryPeriodKind;
  periodKey: string;
  enqueuedAt?: string;
  /**
   * PRODUCT_DEV-698: 個人の下書きか、部署のまとめか。
   * **省略時は `"user"`**（既にキューに積まれているメッセージが従来どおり動く）。
   * `"team"` のときは `userId` を使わず `deptId` の枠を集計する。
   */
  scope?: "user" | "team";
  /** scope="team" のときの部署。`null` は「未所属」枠。 */
  deptId?: string | null;
  /**
   * scope="team" のときの対象メンバー（氏名つき）。呼び出し側が解決して渡す。
   * **氏名まで渡すのは、まとめが「佐藤さんは…」と人を名指しするため。**
   * ワーカー側で引き直すと、集計した瞬間と生成した瞬間で在籍が食い違いうる。
   */
  members?: Array<{ userId: string; name: string }>;
  /**
   * scope="team" のときの期間。**個人と違い「保存済みアイテムの periodFrom」を
   * 当てにできない**（初回はアイテムがまだ無い）ので、積む側が入れる。
   */
  periodFrom?: string;
  periodTo?: string;
}
